/**
 * As queries do painel.
 *
 * Separado do `dados.ts` do inbox de propósito: são **bundles diferentes** (§3 da spec 02), e importar
 * um do outro faria o painel baixar o cliente de SSE e o cache de fio de conversa — que é exatamente o
 * que a separação de build existe para evitar.
 *
 * O que é genuinamente comum (o `chamar`, a sessão, os estados) mora em `@hugi/ui`.
 */

import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { chamar, type FalhaDaApi } from "@hugi/ui";

export interface Conexao {
  readonly id: string;
  readonly canal: string;
  readonly modo: string;
  readonly estado: string;
  /** @cru */
  readonly numeroExibicao: string | null;
  /** "Recepção", "Financeiro". Nulo = sem apelido; a tela mostra o número (migration 0055). */
  readonly apelido: string | null;
  readonly conectadaEm: string | null;
  /** Nulo = o tenant ainda não disse que o número é dele, e nada entra por ele (migration 0053). */
  readonly confirmadaEm: string | null;
  readonly carenciaReaberturaMin: number;
  /**
   * O aquecimento do número novo (0096). Nulo ou ausente = o número já é maduro, ou o canal não aquece
   * (só o aparelho vinculado aquece). `dia` começa em 0; `dispensadoEm` preenchido = alguém dispensou.
   */
  readonly aquecimento?: {
    readonly dia: number;
    readonly dias: number;
    readonly teto: number;
    readonly enviadosHoje: number;
    readonly dispensadoEm: string | null;
  } | null;
}

export interface Motivo {
  readonly id: string;
  readonly nome: string;
  readonly mensagemDeEncerramento: string | null;
  readonly automatico: boolean;
}

export interface Fila {
  readonly id: string;
  readonly nome: string;
  readonly minhas: boolean;
  readonly restritoNela: boolean;
}

export interface Etiqueta {
  readonly id: string;
  readonly nome: string;
  readonly cor: string | null;
}

export interface RespostaRapida {
  readonly id: string;
  readonly atalho: string;
  readonly texto: string;
  readonly visibilidade: string;
}

export interface Catalogos {
  readonly motivos: readonly Motivo[];
  readonly filas: readonly Fila[];
  readonly etiquetas: readonly Etiqueta[];
  readonly respostasRapidas: readonly RespostaRapida[];
}

export const chaves = {
  conexoes: () => ["conexoes"] as const,
  catalogos: () => ["catalogos"] as const,
  perfilDoBot: (conexaoId: string) => ["perfil-do-bot", conexaoId] as const,
  templates: () => ["templates"] as const,
} as const;

export interface Template {
  readonly id: string;
  readonly conexaoId: string;
  readonly nome: string;
  readonly idioma: string;
  readonly categoria: string | null;
  readonly estado: string;
  readonly corpo: string | null;
  readonly variaveis: number;
  /**
   * Os botões do template aprovado. `exigeValor` é o que decide se a campanha precisa preencher algo:
   * botão de URL com `{{n}}` e botão de cupom exigem, e **sem o valor a Meta recusa a mensagem
   * inteira** — não o botão.
   */
  readonly botoes?: readonly {
    readonly indice: number;
    readonly tipo: string;
    readonly texto: string | null;
    readonly exigeValor: boolean;
  }[];
  /** `true` quando o template é um carrossel de cartões. */
  readonly carrossel?: boolean;
  /** `true` em marketing: sempre cobrado pela Meta, e a tela diz isso onde o template aparece. */
  readonly sempreCobrado: boolean;
  /** GREEN | YELLOW | RED | UNKNOWN — a qualidade que a Meta mede por template. `null` = nunca lida. */
  readonly qualidade: string | null;
  /** Bloqueado DENTRO do Hugi (reclassificado, qualidade, pausado pela Meta, manual). Liberar é manual. */
  readonly bloqueio: {
    readonly motivo: string;
    readonly detalhe: string | null;
    readonly desde: string;
    readonly categoriaAnterior: string | null;
  } | null;
}

export async function liberarTemplate(cliente: QueryClient, templateId: string): Promise<{ ok: boolean; falha?: FalhaDaApi }> {
  try {
    await chamar(`/api/painel/templates/${templateId}/liberar`, { metodo: "POST" });
    await cliente.invalidateQueries({ queryKey: ["templates"] });
    return { ok: true };
  } catch (erro) {
    const falha = (erro as { falha?: FalhaDaApi }).falha;
    return falha ? { ok: false, falha } : { ok: false };
  }
}

export async function bloquearTemplate(cliente: QueryClient, templateId: string, motivo: string | null): Promise<{ ok: boolean; falha?: FalhaDaApi }> {
  try {
    await chamar(`/api/painel/templates/${templateId}/bloquear`, { metodo: "POST", corpo: { motivo } });
    await cliente.invalidateQueries({ queryKey: ["templates"] });
    return { ok: true };
  } catch (erro) {
    const falha = (erro as { falha?: FalhaDaApi }).falha;
    return falha ? { ok: false, falha } : { ok: false };
  }
}

/**
 * Os templates do tenant — **todos, numa chamada só**.
 *
 * Sem `?conexao=`, e não uma consulta por cartão: a lista sai do nosso banco (a rota lê
 * `canal_templates`, não a Meta), então buscá-la inteira custa uma consulta e agrupar por
 * `conexaoId` é trabalho da tela. Uma chamada por cartão multiplicaria requisições por um dado que
 * já vem junto.
 *
 * `staleTime` alto pela mesma razão do inbox: a lista muda quando a Meta aprova algo — minutos ou
 * horas. Quem acabou de sincronizar não espera o relógio, porque `sincronizarTemplates` invalida.
 */
export function useTemplates(ligado: boolean) {
  return useQuery({
    queryKey: chaves.templates(),
    queryFn: () => chamar<{ templates: readonly Template[] }>("/api/painel/templates"),
    // Quem liga é a tela, por duas condições: haver conexão que tenha template, e a sessão ter
    // `read_conversation` — que é a permissão desta rota. Sem o segundo, a consulta levaria 403 e a tela
    // mostraria falha onde o certo é dizer qual permissão falta.
    enabled: ligado,
    staleTime: 5 * 60_000,
  });
}

export interface ResultadoDaSincronizacao {
  readonly ok: boolean;
  readonly gravados?: number;
  readonly ausentes?: number;
  readonly falha?: FalhaDaApi;
}

/**
 * Ir buscar os templates na Meta.
 *
 * **A única escrita desta superfície**, e é ela que faltava: a rota existe desde o H10 com
 * `manage_template` e nenhum chamador em `web/`. O cliente não cria template pelo Hugi — quem
 * aprova é a Meta, e isto é o espelho sendo atualizado.
 *
 * Devolve a falha em vez de lançar, como as mutações de catálogo: as quatro recusas nomeadas do
 * servidor (`conexao_sem_waba`, `sem_credencial`, `meta_recusou`, `conexao_nao_encontrada`) são
 * **informação para mostrar no cartão**, não erro para registrar — cada uma diz a quem pertence o
 * problema, e a da Meta vem com a frase dela.
 *
 * Invalida a checklist junto: o passo "Ter um template aprovado" conta `canal_templates`, e sem isso
 * o Início continuaria cobrando o passo que a pessoa acabou de cumprir.
 */
export async function sincronizarTemplates(
  cliente: QueryClient,
  conexaoId: string,
): Promise<ResultadoDaSincronizacao> {
  try {
    const r = await chamar<{ gravados: number; ausentes: number }>(
      `/api/painel/conexoes/${conexaoId}/templates/sincronizar`,
      { metodo: "POST" },
    );
    await cliente.invalidateQueries({ queryKey: chaves.templates() });
    await cliente.invalidateQueries({ queryKey: ["ativacao"] });
    return { ok: true, gravados: r.gravados, ausentes: r.ausentes };
  } catch (erro) {
    const falha = (erro as { falha?: FalhaDaApi }).falha;
    return falha ? { ok: false, falha } : { ok: false };
  }
}

export interface ComandoDoBot {
  readonly comando: string;
  readonly descricao: string;
}

export interface PerfilDoBot {
  readonly nome: string;
  readonly descricao: string;
  readonly descricaoCurta: string;
  readonly comandos: readonly ComandoDoBot[];
}

export interface LimitesDoPerfil {
  readonly nome: number;
  readonly descricao: number;
  readonly descricaoCurta: number;
  readonly comando: number;
  readonly descricaoDoComando: number;
  readonly fotoEmBytes: number;
}

/**
 * O perfil do bot, lido do Telegram na hora.
 *
 * `enabled` sob demanda: são quatro chamadas à Bot API por conexão, e disparar isso ao abrir a tela de
 * Canais pagaria o custo para quem só queria conferir se o número está conectado.
 */
export function usePerfilDoBot(conexaoId: string, ligado: boolean) {
  return useQuery({
    queryKey: chaves.perfilDoBot(conexaoId),
    queryFn: () =>
      chamar<{ perfil: PerfilDoBot; limites: LimitesDoPerfil }>(
        `/api/painel/conexoes/${conexaoId}/perfil-telegram`,
      ),
    enabled: ligado,
    // O perfil vive do lado do Telegram, e pode ser mudado pelo BotFather por fora. Releitura a cada
    // abertura é o certo — cache longo aqui mostraria um nome que já não é o do bot.
    staleTime: 0,
  });
}

export async function salvarPerfilDoBot(
  cliente: QueryClient,
  conexaoId: string,
  corpo: Partial<PerfilDoBot>,
): Promise<{ ok: boolean; falha?: FalhaDaApi }> {
  try {
    await chamar(`/api/painel/conexoes/${conexaoId}/perfil-telegram`, { metodo: "PATCH", corpo });
    await cliente.invalidateQueries({ queryKey: chaves.perfilDoBot(conexaoId) });
    return { ok: true };
  } catch (erro) {
    const falha = (erro as { falha?: FalhaDaApi }).falha;
    return falha ? { ok: false, falha } : { ok: false };
  }
}

export async function trocarFotoDoBot(
  conexaoId: string,
  arquivo: File,
): Promise<{ ok: boolean; falha?: FalhaDaApi }> {
  try {
    // Base64 e não `multipart` daqui: quem exige `multipart` é o Telegram, e quem monta isso é o
    // servidor — que é também quem tem o token. O painel nunca fala com a Bot API.
    const base64 = await new Promise<string>((resolve, reject) => {
      const leitor = new FileReader();
      leitor.onerror = () => reject(new Error("não consegui ler o arquivo"));
      leitor.onload = () => resolve(String(leitor.result).split(",")[1] ?? "");
      leitor.readAsDataURL(arquivo);
    });

    await chamar(`/api/painel/conexoes/${conexaoId}/foto-telegram`, {
      metodo: "POST",
      corpo: { base64, tipoMime: arquivo.type, nome: arquivo.name },
    });
    return { ok: true };
  } catch (erro) {
    const falha = (erro as { falha?: FalhaDaApi }).falha;
    return falha ? { ok: false, falha } : { ok: false };
  }
}

export async function revogarConexao(
  cliente: QueryClient,
  conexaoId: string,
): Promise<{ ok: boolean; avisos?: readonly string[]; falha?: FalhaDaApi }> {
  try {
    const r = await chamar<{ revogada: boolean; jaEstava: boolean; avisos?: readonly string[] }>(
      `/api/painel/conexoes/${conexaoId}/revogar`,
      { metodo: "POST" },
    );
    await cliente.invalidateQueries({ queryKey: chaves.conexoes() });
    // A checklist de ativação conta canal conectado; sem invalidar, o Início continuaria dizendo que
    // este passo está cumprido depois de o cliente ter desligado o único canal.
    await cliente.invalidateQueries({ queryKey: ["ativacao"] });
    return { ok: true, ...(r.avisos ? { avisos: r.avisos } : {}) };
  } catch (erro) {
    const falha = (erro as { falha?: FalhaDaApi }).falha;
    return falha ? { ok: false, falha } : { ok: false };
  }
}

/**
 * Dispensa o aquecimento do número (ou volta a aquecê-lo). Invalidar `conexoes` troca a linha do cartão.
 */
export async function dispensarAquecimento(
  cliente: QueryClient,
  conexaoId: string,
  dispensar: boolean,
): Promise<{ ok: boolean; falha?: FalhaDaApi }> {
  try {
    await chamar(`/api/painel/conexoes/${conexaoId}/aquecimento`, { metodo: "POST", corpo: { dispensar } });
    await cliente.invalidateQueries({ queryKey: chaves.conexoes() });
    return { ok: true };
  } catch (erro) {
    const falha = (erro as { falha?: FalhaDaApi }).falha;
    return falha ? { ok: false, falha } : { ok: false };
  }
}

/**
 * Grava (ou limpa) o apelido da conexão. Vazio limpa — e invalidar `conexoes` é o que faz o cartão
 * trocar o número pelo apelido sem recarregar.
 */
export async function apelidarConexao(
  cliente: QueryClient,
  conexaoId: string,
  apelido: string,
): Promise<{ ok: boolean; falha?: FalhaDaApi }> {
  try {
    await chamar(`/api/painel/conexoes/${conexaoId}/apelido`, {
      metodo: "POST",
      corpo: { apelido: apelido.trim() === "" ? null : apelido },
    });
    await cliente.invalidateQueries({ queryKey: chaves.conexoes() });
    return { ok: true };
  } catch (erro) {
    const falha = (erro as { falha?: FalhaDaApi }).falha;
    return falha ? { ok: false, falha } : { ok: false };
  }
}

export async function removerFotoDoBot(conexaoId: string): Promise<{ ok: boolean; falha?: FalhaDaApi }> {
  try {
    await chamar(`/api/painel/conexoes/${conexaoId}/foto-telegram`, { metodo: "DELETE" });
    return { ok: true };
  } catch (erro) {
    const falha = (erro as { falha?: FalhaDaApi }).falha;
    return falha ? { ok: false, falha } : { ok: false };
  }
}

export function useConexoes() {
  return useQuery({
    queryKey: chaves.conexoes(),
    queryFn: () => chamar<{ conexoes: readonly Conexao[] }>("/api/painel/conexoes"),
  });
}

/**
 * Os motivos de perda do funil.
 *
 * Rota própria, e não um campo a mais em `/catalogos`: quem os pede é o passo `dar_como_perdida` do
 * fluxo, e carregá-los em toda abertura de tela do painel seria uma consulta a mais para todo mundo
 * por causa de um passo que quase nenhum fluxo tem. É o mesmo `useMotivosDePerda` do inbox — a mesma
 * rota, o mesmo `staleTime` —, e não uma segunda leitura.
 */
export function useMotivosDePerda() {
  return useQuery({
    queryKey: ["funil", "motivos-de-perda"],
    queryFn: () => chamar<{ itens: readonly { id: string; nome: string }[] }>("/api/painel/funil/motivos-de-perda"),
    staleTime: 5 * 60_000,
  });
}

export function useCatalogos() {
  return useQuery({
    queryKey: chaves.catalogos(),
    queryFn: () => chamar<Catalogos>("/api/painel/catalogos"),
  });
}

/**
 * Uma mutação de catálogo.
 *
 * **Devolve a falha em vez de lançar**, e é o que a tela precisa: uma recusa por regra — nome repetido,
 * fila com atendimento aberto — é **informação para mostrar no formulário**, não erro para registrar. O
 * `onError` do `useMutation` empurraria as duas para o mesmo caminho.
 *
 * Invalida **sempre**, inclusive na recusa: um 409 significa que o servidor tem um estado diferente do
 * que a tela mostrava, e é aí que revalidar importa mais.
 */
export function useMutacaoDeCatalogo<T = unknown>() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: async (pedido: {
      caminho: string;
      // `PUT` entrou em 2026-08-13 com a troca de perfil de acesso, que substitui
      // um estado inteiro (o papel da pessoa) em vez de mesclar campos.
      metodo?: "POST" | "PATCH" | "PUT" | "DELETE";
      corpo?: unknown;
    }): Promise<{ ok: boolean; dados?: T; falha?: FalhaDaApi }> => {
      try {
        const dados = await chamar<T>(`/api/painel${pedido.caminho}`, {
          metodo: pedido.metodo ?? "POST",
          ...(pedido.corpo === undefined ? {} : { corpo: pedido.corpo }),
        });
        return { ok: true, dados };
      } catch (erro) {
        const falha = (erro as { falha?: FalhaDaApi }).falha;
        return falha ? { ok: false, falha } : { ok: false };
      }
    },
    onSettled: () => cliente.invalidateQueries({ queryKey: chaves.catalogos() }),
  });
}

// --- Início, contatos, relatórios e integrações (H24/H25) ---------------------------------------

export interface PassoDaAtivacao {
  readonly id: string;
  readonly rotulo: string;
  readonly concluido: boolean;
  readonly detalhe: string | null;
  /** `true` quando o passo depende da Meta, e não do cliente. A tela não cobra o que ele não resolve. */
  readonly dependeDeTerceiro: boolean;
}

export function useAtivacao() {
  return useQuery({
    queryKey: ["ativacao"],
    queryFn: () => chamar<{ passos: PassoDaAtivacao[]; concluida: boolean }>("/api/painel/ativacao"),
  });
}

/** O que o Início mostra depois da ativação — o contrato é o de `src/painel/inicio.ts`. */
export interface ResumoDoInicio {
  readonly hoje: { readonly recebidas: number; readonly respondidas: number; readonly finalizadas: number } | null;
  readonly esperando: { readonly agora: number; readonly haMaisDeMin: number; readonly limiteMin: number; readonly maisAntigaSegundos: number | null } | null;
  readonly canais: {
    readonly total: number;
    readonly noAr: number;
    readonly foraDoAr: readonly {
      readonly id: string;
      /** @cru */
      readonly rotulo: string;
      readonly motivo: "socket_caido" | "processo_sumiu" | "transporte_parado" | "degradada" | "sem_confirmacao";
      readonly desde: string | null;
    }[];
    /**
     * SAÚDE (spec 19): os números que merecem ser olhados hoje. Lido do último ponto GRAVADO — o
     * Início não refaz a conta da tela de saúde, e por isso mostra o mesmo número que ela.
     *
     * Opcional porque um servidor mais velho não o manda, e a tela não pode quebrar por isso.
     */
    readonly saude?: readonly {
      readonly conexaoId: string;
      /** @cru */
      readonly rotulo: string;
      readonly indice: number | null;
      readonly faixa: string;
      readonly punicoes: readonly string[];
      readonly variacao: number | null;
      readonly motivo: "punida" | "risco" | "caindo";
    }[];
  } | null;
  readonly fluxos: { readonly total: number; readonly atendendo: number } | null;
  readonly agentes: { readonly total: number; readonly noAr: number } | null;
  readonly campanhas: { readonly emVoo: number; readonly pausadas: readonly { readonly id: string; readonly nome: string; readonly motivo: string | null }[] } | null;
  readonly publicacoes: {
    readonly agendadas: number;
    readonly precisamDeAtencao: readonly { readonly id: string; readonly nome: string; readonly estado: string; readonly falhados: number }[];
  } | null;
  readonly funil: { readonly abertas: number } | null;
}

export function useResumoDoInicio() {
  return useQuery({
    queryKey: ["inicio"],
    queryFn: () => chamar<ResumoDoInicio>("/api/painel/inicio"),
    // A fila e o vigia mudam a cada volta de relógio; o Início acompanha sem a pessoa recarregar.
    refetchInterval: 30_000,
  });
}

export interface ContatoNaLista {
  readonly id: string;
  readonly canal: string;
  /** @cru */
  readonly identificador: string;
  readonly nome: string | null;
  readonly nomeDoPerfil: string | null;
  readonly primeiroContatoEm: string | null;
  readonly ultimoContatoEm: string | null;
  readonly atendimentos: number;
  readonly abertos: number;
  readonly bloqueio: string | null;
  readonly identificadorInvalido: boolean;
  readonly etiquetas: readonly { id: string; nome: string; cor: string | null }[];
  /** Opt-in registrado (spec 17). `null` = campanha não alcança este contato. */
  readonly optinEm: string | null;
  readonly optinOrigem: string | null;
}

/** Opt-in registrado pela equipe (spec 17 §7). */
export async function registrarOptin(cliente: QueryClient, contatoId: string, nota: string | null): Promise<{ ok: boolean; falha?: FalhaDaApi }> {
  try {
    await chamar(`/api/painel/contatos/${contatoId}/optin`, { metodo: "POST", corpo: { origem: "declarado_pelo_cliente", nota } });
    await cliente.invalidateQueries({ queryKey: ["contatos"] });
    return { ok: true };
  } catch (erro) {
    const falha = (erro as { falha?: FalhaDaApi }).falha;
    return falha ? { ok: false, falha } : { ok: false };
  }
}

export async function bloquearContato(cliente: QueryClient, contatoId: string, motivo: "opt_out" | "manual"): Promise<{ ok: boolean; falha?: FalhaDaApi }> {
  try {
    await chamar(`/api/painel/contatos/${contatoId}/bloqueio`, { metodo: "POST", corpo: { motivo } });
    await cliente.invalidateQueries({ queryKey: ["contatos"] });
    return { ok: true };
  } catch (erro) {
    const falha = (erro as { falha?: FalhaDaApi }).falha;
    return falha ? { ok: false, falha } : { ok: false };
  }
}

export async function desbloquearContato(cliente: QueryClient, contatoId: string): Promise<{ ok: boolean; falha?: FalhaDaApi }> {
  try {
    await chamar(`/api/painel/contatos/${contatoId}/bloqueio`, { metodo: "DELETE" });
    await cliente.invalidateQueries({ queryKey: ["contatos"] });
    return { ok: true };
  } catch (erro) {
    const falha = (erro as { falha?: FalhaDaApi }).falha;
    return falha ? { ok: false, falha } : { ok: false };
  }
}

export function useContatos(busca: string, etiquetaId: string | null) {
  return useQuery({
    queryKey: ["contatos", busca, etiquetaId],
    queryFn: () => {
      const q = new URLSearchParams();
      if (busca.trim()) q.set("busca", busca.trim());
      if (etiquetaId) q.set("etiqueta", etiquetaId);
      return chamar<{ itens: ContatoNaLista[] }>(`/api/painel/contatos?${q}`);
    },
  });
}

export interface Tempos {
  readonly medianaSegundos: number | null;
  readonly p90Segundos: number | null;
  readonly amostra: number;
}

export interface RelatorioDeVolume {
  readonly periodo: { dias: number };
  readonly recebidas: number;
  readonly respondidas: number;
  readonly finalizadas: number;
  readonly reabertas: number;
  readonly aguardandoAgora: number;
  readonly emAtendimentoAgora: number;
  readonly espera: Tempos;
  readonly atendimento: Tempos;
  readonly primeiraResposta: Tempos;
  readonly porCanal: readonly { canal: string; atendimentos: number; esperaMediana: number | null; primeiraRespostaMediana: number | null }[];
  readonly porConexao: readonly { conexaoId: string | null; /** @cru */ rotulo: string; canal: string; atendimentos: number }[];
  readonly porFila: readonly { filaId: string | null; nome: string; atendimentos: number }[];
  readonly porAtendente: readonly { readonly nome: string; atendenteId: string; atendimentos: number; atendimentoMediana: number | null }[];
  readonly porMotivo: readonly { motivoId: string | null; nome: string; automatico: boolean; atendimentos: number }[];
}

export interface RelatorioDeOrigem {
  readonly total: number;
  readonly deAnuncio: number;
  readonly porAnuncio: readonly { anuncioId: string | null; titulo: string | null; tipoDeOrigem: string | null; conversas: number; respondidas: number }[];
}

/** Uma linha da quebra por categoria de cobrança. Espelha `LinhaDeCategoria` do servidor. */
export interface CategoriaDeCobranca {
  readonly categoria: string;
  /** Já vem em português do servidor; categoria nova chega com o valor cru, e não some. */
  readonly rotulo: string;
  readonly mensagens: number;
  readonly cobraveis: number;
}

export interface RelatorioDeConsumo {
  readonly enviadas: number;
  readonly recebidas: number;
  readonly porTemplate: number;
  readonly cobraveisConhecidas: number;
  readonly porCategoria: readonly CategoriaDeCobranca[];
  /** Envios sem categoria — **não é uma categoria**, e por isso vem fora da lista. */
  readonly semCategoria: number;
  readonly porqueSemCategoria: string;
  /** A lacuna do §5.13(c), dita pelo servidor. A tela mostra, não reescreve. */
  readonly lacuna: string;
}

/** O que `GET /api/painel/plano` devolve. Espelha `src/painel/plano.ts`. */
export interface ConsumoDoPlano {
  readonly competencia: string;
  readonly plano: string | null;
  readonly numeros: number;
  /** Quantos ele CONTRATOU. Nulo = ninguém registrou, e aí a franquia usa os conectados. */
  readonly numerosContratados: number | null;
  readonly conversasIncluidasPorNumero: number | null;
  readonly franquiaTotal: number | null;
  readonly conversas: number;
  readonly porNumero: readonly {
    readonly conexaoId: string;
    /** @cru */
    readonly numeroExibicao: string | null;
    readonly canal: string;
    readonly conversas: number;
  }[];
  readonly fracao: number | null;
  readonly excedente: number;
  readonly excedentePorConversaCentavos: number | null;
  readonly excedenteEstimadoCentavos: number | null;
  readonly situacao: "indefinido" | "ok" | "perto" | "estourou";
  /** A frase vem do SERVIDOR. Uma verdade só — ver o cabeçalho de `plano.ts`. */
  readonly explicacao: string;
  /** Publicações em Grupos: o direito e o teto POR EMPRESA (não por número), como o Cert4All declara. */
  readonly publicacoesEmGrupos: boolean;
  readonly gruposIncluidos: number | null;
  /** A franquia de IA (ADR-0018). `franquiaIa` nulo = o plano não a define. */
  readonly agenteIncluido: boolean | null;
  readonly conversasDeIaIncluidasPorNumero: number | null;
  readonly franquiaIa: number | null;
  readonly conversasIa: number;
  readonly fracaoIa: number | null;
  readonly excedenteIa: number;
  readonly excedenteIaPorConversaCentavos: number | null;
  readonly excedenteIaEstimadoCentavos: number | null;
  readonly situacaoIa: "indefinido" | "ok" | "perto" | "estourou";
  readonly excedenteDeIaAutorizado: boolean;
  readonly tetoDeGastoIaCentavos: number | null;
  readonly agenteParado: boolean;
  readonly explicacaoIa: string;
}

/** O opt-in do excedente de IA e o teto de gasto (ADR-0018 §4). Devolve o consumo atualizado. */
export function useDefinirExcedenteDeIa() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: { autorizado: boolean; tetoDeGastoCentavos: number | null }) =>
      chamar<ConsumoDoPlano>("/api/painel/plano/excedente-de-ia", { metodo: "POST", corpo: p }),
    onSuccess: (consumo) => qc.setQueryData(["plano"], consumo),
  });
}

export function useConsumoDoPlano() {
  return useQuery({
    queryKey: ["plano"],
    queryFn: () => chamar<ConsumoDoPlano>("/api/painel/plano"),
  });
}

/**
 * COMPRAR MAIS NÚMEROS.
 *
 * ⚠️ A CHAVE DE IDEMPOTÊNCIA NASCE NA TELA e sobrevive à retentativa. É ela que faz "apertei o botão
 * duas vezes" ser **uma** cobrança: o servidor reencontra a operação e devolve o resultado dela em
 * vez de cobrar de novo. Gerá-la a cada envio recriaria exatamente o defeito.
 *
 * ⚠️ E `numerosContratados` é o TOTAL desejado, nunca o incremento.
 */
export function useComprarNumeros() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: { numerosContratados: number; chaveDeIdempotencia: string }) =>
      chamar<{
        numerosContratados: number;
        proporcionalCentavos: number;
        novoValorMensalCentavos: number;
        cobrancaId: string | null;
        jaEstavaFeito: boolean;
        explicacao: string;
      }>("/api/painel/plano/numeros", { metodo: "POST", corpo: p }),
    // O medidor inteiro muda: franquia, fração, situação e a frase. Reler é mais barato que remendar.
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["plano"] }),
  });
}

/**
 * A ASSINATURA, lida do Cert4All pelo servidor do Hugi.
 *
 * ⚠️ O produto não guarda assinatura nenhuma — o Hugi repassa com o token da pessoa. Ver
 * `src/painel/assinaturaDoCert4All.ts`.
 */
export type Assinatura =
  | {
      tipo: "viva";
      assinatura: { plano: string; ciclo: "mensal" | "anual"; pagoCentavos: number; inicioEm: string; renovaEm: string };
      seCancelarAgora: {
        mesesRetidos: number;
        retidoCentavos: number;
        reembolsoCentavos: number;
        acessoAte: string;
        arrependimento: boolean;
        motivo: string;
      };
    }
  | {
      tipo: "cancelada";
      cancelamento: { motivo: string; acessoAte: string; reembolsoCentavos: number; estornadoEm: string | null };
      /* Quem responde é o servidor: só ele sabe se o encerramento já saiu da fila. */
      podeReativar: boolean;
    }
  | { tipo: "sem_assinatura" }
  | { tipo: "indisponivel"; motivo: string };

export function useAssinatura() {
  return useQuery({
    queryKey: ["assinatura"],
    queryFn: () => chamar<Assinatura>("/api/painel/assinatura"),
  });
}

export function useCancelarAssinatura() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      chamar<{
        tipo: "cancelada";
        reembolsoCentavos: number;
        acessoAte: string;
        motivo: string;
        avisos: string[];
      }>("/api/painel/assinatura/cancelar", { metodo: "POST" }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["assinatura"] }),
  });
}

export function useReativarAssinatura() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      chamar<{ tipo: "reativada"; acessoAte: string; avisos: string[] }>(
        "/api/painel/assinatura/reativar",
        { metodo: "POST" },
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["assinatura"] }),
  });
}

export function useRelatorioDeVolume(dias: number) {
  return useQuery({
    queryKey: ["relatorio", "volume", dias],
    queryFn: () => chamar<RelatorioDeVolume>(`/api/painel/relatorios/volume?dias=${dias}`),
  });
}

export function useRelatorioDeOrigem(dias: number) {
  return useQuery({
    queryKey: ["relatorio", "origem", dias],
    queryFn: () => chamar<RelatorioDeOrigem>(`/api/painel/relatorios/origem?dias=${dias}`),
  });
}

export function useRelatorioDeConsumo(dias: number) {
  return useQuery({
    queryKey: ["relatorio", "consumo", dias],
    queryFn: () => chamar<RelatorioDeConsumo>(`/api/painel/relatorios/consumo?dias=${dias}`),
  });
}

/**
 * A satisfação: distribuição e taxa de resposta, **nunca média**.
 *
 * Média de nota com poucas respostas mente nos dois sentidos — duas notas 5 e uma nota 1 dão 3,67, que não
 * descreve nenhum dos três clientes. É a mesma decisão que já vale para tempo (mediana e p90).
 */
export interface Satisfacao {
  readonly pedidas: number;
  readonly respondidas: number;
  /** Cinco posições, nota 1 a 5. */
  readonly distribuicao: readonly number[];
  /** `null` quando ninguém respondeu — e `null` não é 0%, que diria "todos odiaram". */
  readonly percentualBom: number | null;
  readonly comentarios: readonly {
    readonly nota: number;
    readonly comentario: string;
    readonly atendenteId: string | null;
    readonly em: string;
  }[];
}

export function useSatisfacao(dias: number) {
  return useQuery({
    queryKey: ["relatorio", "satisfacao", dias],
    queryFn: () => chamar<Satisfacao>(`/api/painel/relatorios/satisfacao?dias=${dias}`),
  });
}

export interface ChaveDeApi {
  readonly id: string;
  readonly nome: string;
  readonly prefixo: string;
  readonly escopos: readonly string[];
  readonly criadaEm: string;
  readonly ultimoUsoEm: string | null;
  readonly revogadaEm: string | null;
}

export interface WebhookDeSaida {
  readonly id: string;
  readonly url: string;
  readonly eventos: readonly string[];
  readonly ativo: boolean;
  readonly falhandoDesde: string | null;
  readonly desativadoEm: string | null;
  readonly desativadoMotivo: string | null;
  readonly criadoEm: string;
}

export interface EntregaDeWebhook {
  readonly id: string;
  readonly evento: string;
  readonly tentativa: number;
  readonly statusHttp: number | null;
  readonly erro: string | null;
  readonly duracaoMs: number | null;
  readonly proximaTentativaEm: string | null;
  readonly entregueEm: string | null;
  readonly criadaEm: string;
}

export function useChaves() {
  return useQuery({
    queryKey: ["chaves"],
    queryFn: () => chamar<{ chaves: ChaveDeApi[] }>("/api/painel/chaves"),
  });
}

export function useWebhooks() {
  return useQuery({
    queryKey: ["webhooks"],
    queryFn: () => chamar<{ webhooks: WebhookDeSaida[] }>("/api/painel/webhooks"),
  });
}

export function useEntregas(webhookId: string | null) {
  return useQuery({
    queryKey: ["entregas", webhookId],
    queryFn: () => chamar<{ entregas: EntregaDeWebhook[] }>(`/api/painel/webhooks/${webhookId}/entregas`),
    enabled: Boolean(webhookId),
  });
}

/** Segundos → "4 min", "2h 10min". Mesma forma do inbox, para os dois não divergirem. */
export function duracaoCurta(segundos: number | null): string {
  if (segundos === null) return "—";
  if (segundos < 60) return `${Math.round(segundos)}s`;
  if (segundos < 3600) return `${Math.round(segundos / 60)} min`;
  const h = Math.floor(segundos / 3600);
  const m = Math.round((segundos % 3600) / 60);
  return m === 0 ? `${h}h` : `${h}h ${m}min`;
}

export interface LinhaDoHistorico {
  readonly id: string;
  readonly protocolo: string;
  readonly canal: string;
  readonly contato: string;
  /** @cru */
  readonly identificador: string;
  readonly fila: string | null;
  /** `null` quando quem atendeu foi o AGENTE — a tela diz "agente", não um traço. */
  readonly atendenteId: string | null;
  readonly motivo: string | null;
  readonly motivoAutomatico: boolean;
  readonly abertoEm: string;
  readonly assumidoEm: string | null;
  readonly finalizadoEm: string | null;
  readonly esperaSegundos: number | null;
  readonly atendimentoSegundos: number | null;
  readonly etiquetas: readonly string[];
  readonly mensagens: number;
  /** `true` quando o texto já venceu (ADR-0003). A tela e a exportação dizem isso. */
  readonly conteudoExpurgado: boolean;
}

export function useHistorico(filtro: {
  dias: number;
  busca: string;
  filaId: string;
  motivoId: string;
}) {
  return useQuery({
    queryKey: ["historico", filtro.dias, filtro.busca, filtro.filaId, filtro.motivoId],
    queryFn: () => {
      const q = new URLSearchParams({ dias: String(filtro.dias) });
      if (filtro.busca.trim()) q.set("busca", filtro.busca.trim());
      if (filtro.filaId) q.set("fila", filtro.filaId);
      if (filtro.motivoId) q.set("motivo", filtro.motivoId);
      return chamar<{ itens: LinhaDoHistorico[] }>(`/api/painel/historico?${q}`);
    },
  });
}

export interface TelegramConectado {
  readonly conexaoId: string;
  readonly bot: string;
  /** O link que o cliente divulga. É o análogo do QR: sem ele, ninguém escreve para o bot. */
  readonly link: string;
  readonly webhookRegistrado: boolean;
  readonly avisoDoWebhook?: string;
}

/**
 * Conecta um bot de Telegram.
 *
 * O token vai no corpo e **nunca volta**. Não usa `useMutation` pelo mesmo motivo de `operar`: a tela
 * precisa distinguir "recusado com motivo" de "falhou", e o `onError` empurraria os dois para o mesmo
 * caminho.
 */
export async function conectarTelegram(
  cliente: QueryClient,
  token: string,
): Promise<{ ok: boolean; dados?: TelegramConectado; falha?: FalhaDaApi }> {
  try {
    const dados = await chamar<TelegramConectado>("/api/painel/conexoes/telegram", {
      metodo: "POST",
      corpo: { token },
    });
    await cliente.invalidateQueries({ queryKey: ["conexoes"] });
    await cliente.invalidateQueries({ queryKey: ["ativacao"] });
    return { ok: true, dados };
  } catch (erro) {
    const falha = (erro as { falha?: FalhaDaApi }).falha;
    return falha ? { ok: false, falha } : { ok: false };
  }
}

/* ---------------------------------- WhatsApp por QR Code ---------------------------------- */

export type EstadoDoPareamento = "abrindo" | "aguardando_leitura" | "pareado" | "falhou";

export interface PareamentoNaTela {
  readonly id: string;
  readonly estado: EstadoDoPareamento;
  /** O QR **já desenhado**, em SVG. A tela não codifica nada — quem desenha é o servidor. */
  readonly qrSvg?: string;
  readonly expiraEm?: string;
  readonly numero?: string;
  readonly motivo?: string;
  /** O motivo em português, para quem está com o telefone na mão. */
  readonly explicacao?: string;
}

/**
 * Emite o LINK para conectar noutro aparelho.
 *
 * ## Por que ele existe
 *
 * O pareamento por QR precisa de **duas telas** — ninguém fotografa o próprio celular. Quem comprou
 * o Profissional muitas vezes tem uma só, e a tela de Conectar do painel, sozinha, não resolve esse
 * caso: ela mostra o QR na mesma tela em que a pessoa está.
 *
 * O link leva a uma página pública que consome um token de **uso único**, e lá o pareamento oferece
 * o CÓDIGO (`PairPhone`) antes do QR.
 *
 * ## O token vai no FRAGMENTO
 *
 * `#t=` e não `?t=`: o fragmento não é enviado ao servidor, então não entra em log de acesso, log
 * de CDN nem `Referer`. Não adianta manter o QR fora do log e mandar a chave dele por um canal que
 * loga.
 */
export async function emitirLinkDePareamento(): Promise<
  { ok: true; url: string; expiraEm: string } | { ok: false; falha?: FalhaDaApi }
> {
  try {
    const dados = await chamar<{ token: string; expiraEm: string }>(
      "/api/painel/conexoes/whatsapp/link-de-pareamento",
      { metodo: "POST", corpo: {} },
    );
    /*
      A URL é montada AQUI, e não no servidor: ele não conhece o endereço público do site, e
      inventá-lo produziria um link que não abre. O site e o painel são origens diferentes, então o
      endereço é absoluto e vem do build.
    */
    const base = (import.meta.env["VITE_SITE_BASE"] as string | undefined) ?? "https://hugi.com.br";
    return {
      ok: true,
      url: `${base.replace(/\/+$/, "")}/conectar.html#t=${encodeURIComponent(dados.token)}`,
      expiraEm: dados.expiraEm,
    };
  } catch (erro) {
    const falha = (erro as { falha?: FalhaDaApi }).falha;
    return falha ? { ok: false, falha } : { ok: false };
  }
}

/** Pede um pareamento. O QR não vem nesta resposta — ele chega pela consulta de andamento. */
export interface IntencaoDeConexao {
  /** O link hospedado pela Meta. O cliente conclui LÁ, e nós ficamos sabendo por webhook. */
  readonly url: string;
  /** Quando a intenção vence. Passado disso, um onboarding que chegar não é atribuído a ninguém. */
  readonly expiraEm: string;
}

/**
 * Abre a intenção e devolve o link.
 *
 * ⚠️ **Isto NÃO conecta nada** — o desfecho chega depois, pelo webhook `PARTNER_ADDED`, e a tela
 * reage pelo SSE. Uma função chamada `conectar` que não conecta seria mentira de nome, e quem a
 * lesse depois esperaria resposta síncrona.
 */
export async function abrirIntencaoDeConexao(): Promise<
  { ok: true; dados: IntencaoDeConexao } | { ok: false; falha?: FalhaDaApi }
> {
  try {
    const dados = await chamar<IntencaoDeConexao>("/api/painel/conexoes/whatsapp/intencao", {
      metodo: "POST",
      corpo: {},
    });
    return { ok: true, dados };
  } catch (erro) {
    const falha = (erro as { falha?: FalhaDaApi }).falha;
    return falha ? { ok: false, falha } : { ok: false };
  }
}

/*
 * Havia aqui `ehWhatsAppOficial` e `useEsperandoWhatsApp`, que esperavam uma conexão NOVA aparecer
 * comparando a lista de conexões antes e depois. **Removidos em 2026-09-02**: o sinal virou a
 * conexão PENDENTE DE CONFIRMAÇÃO (`useAguardandoConfirmacao`), que é mais simples e mais
 * verdadeiro — quem está pendente é exatamente quem precisa da pergunta, e não há retrato para sair
 * errado quando a lista ainda não carregou.
 */

export interface ConexaoAguardandoConfirmacao {
  readonly id: string;
  /** @cru */
  readonly numeroExibicao: string | null;
  readonly wabaId: string | null;
  readonly chegouEm: string | null;
}

/**
 * As conexões que chegaram pela Meta e esperam o tenant dizer que são dele.
 *
 * ⚠️ **Este é o sinal certo de "chegou", e substituiu o retrato de ids.** A versão anterior
 * comparava a lista de conexões antes e depois — o que respondia "apareceu alguma" mas não
 * "apareceu a MINHA". Perguntar direto pelas pendentes é mais simples e mais verdadeiro: quem está
 * pendente é exatamente quem precisa da pergunta.
 */
export function useAguardandoConfirmacao(ligado: boolean) {
  return useQuery({
    queryKey: ["conexoes", "aguardando-confirmacao"],
    queryFn: () =>
      chamar<{ conexoes: readonly ConexaoAguardandoConfirmacao[] }>(
        "/api/painel/conexoes/aguardando-confirmacao",
      ),
    enabled: ligado,
    refetchInterval: (q) => (q.state.data?.conexoes.length ? false : 3_000),
    // O cliente passa o fluxo na aba DA META. Sem isto a tela fica parada exatamente enquanto
    // precisa observar.
    refetchIntervalInBackground: true,
  });
}

/**
 * O tenant diz que o número é dele — ou que não é.
 *
 * Enquanto ninguém responde, a conexão **não resolve tenant**: mensagem que chegue por ela é gravada
 * sem dono. É o que impede o WhatsApp de uma empresa de nascer na conta de outra quando o
 * `PARTNER_ADDED` chega sem dizer de quem é.
 */
export async function responderConfirmacao(
  cliente: QueryClient,
  conexaoId: string,
  recusar: boolean,
): Promise<{ ok: boolean; falha?: FalhaDaApi }> {
  try {
    await chamar(`/api/painel/conexoes/${conexaoId}/confirmar`, {
      metodo: "POST",
      corpo: { recusar },
    });
    await cliente.invalidateQueries({ queryKey: ["conexoes"] });
    await cliente.invalidateQueries({ queryKey: ["ativacao"] });
    return { ok: true };
  } catch (erro) {
    const falha = (erro as { falha?: FalhaDaApi }).falha;
    return falha ? { ok: false, falha } : { ok: false };
  }
}

export async function pedirPareamento(): Promise<
  { ok: true; pareamentoId: string } | { ok: false; falha?: FalhaDaApi }
> {
  try {
    const dados = await chamar<{ pareamentoId: string }>("/api/painel/conexoes/whatsapp/parear", {
      metodo: "POST",
      corpo: {},
    });
    return { ok: true, pareamentoId: dados.pareamentoId };
  } catch (erro) {
    const falha = (erro as { falha?: FalhaDaApi }).falha;
    return falha ? { ok: false, falha } : { ok: false };
  }
}

/**
 * O andamento, por PERGUNTA e não por SSE.
 *
 * O barramento de eventos entrega o que interessa a quem está atendendo; um QR é credencial de vida
 * curta que interessa a uma aba só — a de quem clicou. `refetchInterval` de 1,5 s cobre a troca de
 * código do WhatsApp (a cada ~20 s) com folga, e a consulta para sozinha quando o pareamento termina.
 */
export function usePareamento(id: string | null) {
  return useQuery({
    queryKey: ["pareamento", id],
    enabled: id !== null,
    queryFn: () => chamar<PareamentoNaTela>(`/api/painel/conexoes/pareamento/${id!}`),
    refetchInterval: (q) => {
      const e = q.state.data?.estado;
      return e === "pareado" || e === "falhou" ? false : 1_500;
    },
    // Sem cache entre pareamentos: um QR de um pedido anterior aparecendo por um instante mandaria a
    // pessoa ler um código que já não vale.
    gcTime: 0,
    retry: false,
  });
}

/** Desiste. Melhor esforço dos dois lados: falhar aqui não pode travar a tela de quem desistiu. */
export async function cancelarPareamento(cliente: QueryClient, id: string): Promise<void> {
  await chamar(`/api/painel/conexoes/pareamento/${id}/cancelar`, { metodo: "POST", corpo: {} }).catch(
    () => undefined,
  );
  await cliente.invalidateQueries({ queryKey: ["conexoes"] });
}

export interface ConfiguracaoDoTenant {
  readonly saudacaoLigada: boolean;
  readonly saudacaoTexto: string | null;
  readonly ausenciaLigada: boolean;
  readonly ausenciaTexto: string | null;
  /** Sete posições, domingo a sábado. `null` = fechado naquele dia. */
  readonly horario: readonly (string | null)[] | null;
  readonly fuso: string;
  readonly inatividadeMinutos: number | null;
  readonly inatividadeAvisa: boolean;
  readonly inatividadeTexto: string | null;
  readonly carteirizacao: boolean;
  /** Quem pode COMEÇAR um chat da equipe (0058). */
  readonly chatQuemInicia: "todos" | "supervisores";
  readonly ocultarTelefone: boolean;
  readonly assinarComNome: boolean;
  readonly assinatura: "nunca" | "sempre" | "atendente_escolhe";
  /** WhatsApp pessoal do dono do negocio, so digitos. Nulo = ninguem para avisar. */
  readonly numeroParaAlertas: string | null;
  readonly avaliacaoLigada: boolean;
  readonly avaliacaoTexto: string | null;
  /** A mensagem a quem LIGA no número por QR (0115). Nasce desligada. */
  readonly ligacaoRespostaLigada: boolean;
  readonly ligacaoRespostaTexto: string | null;
  readonly atualizadaEm: string | null;
  readonly atualizadaPor: string | null;
}

/** O que cada canal faz com mensagem automática (§7.3 da spec 05). Vem do servidor. */
export interface AvisoDeCanal {
  readonly canal: string;
  readonly aviso: string;
}

export function useConfiguracoes() {
  return useQuery({
    queryKey: ["configuracoes"],
    queryFn: () =>
      chamar<{ configuracao: ConfiguracaoDoTenant; avisos: AvisoDeCanal[] }>("/api/painel/configuracoes"),
  });
}

/**
 * Grava a configuração inteira.
 *
 * `PUT`, e o corpo é o formulário completo: o servidor SUBSTITUI em vez de fazer merge, porque "desligar a
 * saudação" e "não mencionar a saudação" precisam ser distinguíveis.
 */
export async function salvarConfiguracoes(
  cliente: QueryClient,
  corpo: Partial<ConfiguracaoDoTenant>,
): Promise<{ ok: boolean; falha?: FalhaDaApi }> {
  try {
    await chamar("/api/painel/configuracoes", { metodo: "PATCH", corpo });
    await cliente.invalidateQueries({ queryKey: ["configuracoes"] });
    return { ok: true };
  } catch (erro) {
    const falha = (erro as { falha?: FalhaDaApi }).falha;
    return falha ? { ok: false, falha } : { ok: false };
  }
}

// --- Equipe e acessos (§5.7) ---------------------------------------------------------------------

export interface ConvitePendente {
  readonly id: string;
  readonly email: string;
  readonly expiraEm: string | null;
}

export interface AcessoDaPessoa {
  readonly atendenteId: string;
  readonly iamUserId: string;
  readonly email: string;
  readonly nome: string | null;
  readonly perfil: string | null;
  readonly papel: string | null;
  readonly editavel: boolean;
  readonly motivo: string | null;
  readonly grupos: readonly string[];
}


export interface AtendenteNaLista {
  readonly atendenteId: string;
  readonly nome: string | null;
  readonly ativo: boolean;
  readonly desativadoEm: string | null;
  readonly podeApagarMensagem: boolean;
  readonly podeVerHistorico: boolean;
  readonly restritoAsFilas: boolean;
  /** `null` = segue o horário do tenant. Sete `null` = não trabalha nunca. */
  readonly jornada: readonly (string | null)[] | null;
  readonly liberadoAte: string | null;
  /** `responsavel`: ela supervisiona aquele time (H129, ADR-0022). Opcional: servidor antigo não manda. */
  readonly filas: readonly { id: string; nome: string; responsavel?: boolean }[];
  readonly atendimentosAbertos: number;
  readonly finalizados30d: number;
  /** Já atende e nunca foi registrado nesta tela. Aparece igual — senão seria invisível. */
  readonly semRegistro: boolean;
  /* H129 (spec 24) — todos opcionais: a tela tolera o servidor que ainda não os manda. */
  readonly tempoMedio30dSegundos?: number | null;
  readonly presenca?: EstadoDePresenca;
  readonly presencaMotivo?: string | null;
  readonly limiteSimultaneo?: number | null;
  /** A disponibilidade efetiva (presença manual + jornada + turno), calculada no servidor. */
  readonly disponibilidade?: Disponibilidade;
  /** Com o atendimento aberto e visível agora (C5). `null` = desconhecido. */
  readonly naTela?: boolean | null;
  /** O papel dela no Cert4All supervisiona? `null` = o diretório não respondeu. */
  readonly podeSupervisionar?: boolean | null;
}

export type EstadoDePresenca = "disponivel" | "pausa" | "offline";

export interface Disponibilidade {
  readonly estado: EstadoDePresenca | "inativo";
  readonly motivo: string | null;
  readonly origem: "manual" | "jornada" | "padrao";
}

/** Um membro no cartão do time (`GET /api/painel/equipe/times`). */
export interface MembroDoTime {
  readonly atendenteId: string;
  readonly nome: string | null;
  readonly responsavel: boolean;
  readonly naDistribuicao: boolean;
  readonly disponibilidade: Disponibilidade;
  readonly naTela: boolean | null;
  readonly abertos: number;
  readonly limite: number | null;
}

export interface TimeNaLista {
  readonly id: string;
  readonly nome: string;
  readonly responsavelDistribui: boolean;
  readonly membros: readonly MembroDoTime[];
  readonly agora: { readonly esperando: number; readonly maisAntigaEm: string | null; readonly foraDoPrazo: number; readonly emAtendimento: number };
  readonly ia: { readonly agenteId: string; readonly nome: string; readonly atende: string; readonly segurando: number; readonly passouHoje: number } | null;
}

export interface Times {
  readonly times: readonly TimeNaLista[];
  readonly motivosDePausa: readonly string[];
}

/** Os times (H129). Atualiza sozinho a cada 20 s: presença e carga mudam sem ninguém clicar. */
export function useTimes(ligado = true) {
  return useQuery({
    queryKey: ["equipe", "times"],
    queryFn: () => chamar<Times>("/api/painel/equipe/times"),
    enabled: ligado,
    refetchInterval: 20_000,
  });
}

async function escreverNaEquipe(
  cliente: QueryClient,
  caminho: string,
  opcoes: { metodo: "POST" | "PUT"; corpo?: unknown },
): Promise<{ ok: boolean; dados?: Record<string, unknown>; falha?: FalhaDaApi }> {
  try {
    const dados = await chamar<Record<string, unknown>>(caminho, opcoes);
    await cliente.invalidateQueries({ queryKey: ["equipe"] });
    await cliente.invalidateQueries({ queryKey: chaves.catalogos() });
    await cliente.invalidateQueries({ queryKey: ["atendimentos"] });
    return { ok: true, dados };
  } catch (erro) {
    const falha = (erro as { falha?: FalhaDaApi }).falha;
    return falha ? { ok: false, falha } : { ok: false };
  }
}

/** Quem supervisiona o time (`manage_team`; só Administrador ou Supervisor do Cert4All). */
export function definirResponsaveis(cliente: QueryClient, filaId: string, responsaveis: readonly string[], responsavelDistribui?: boolean) {
  return escreverNaEquipe(cliente, `/api/painel/filas/${encodeURIComponent(filaId)}/responsaveis`, {
    metodo: "PUT",
    corpo: { responsaveis, ...(responsavelDistribui === undefined ? {} : { responsavelDistribui }) },
  });
}

/** Criar time = fila + responsáveis + membros, num passo. */
export function criarTime(
  cliente: QueryClient,
  corpo: { nome: string; responsaveis: readonly string[]; membros: readonly string[]; responsavelDistribui: boolean },
) {
  return escreverNaEquipe(cliente, "/api/painel/equipe/times", { metodo: "POST", corpo });
}

/** Disponível, Em pausa (com motivo) ou Offline — a própria ou de quem se supervisiona. */
export function mudarPresenca(cliente: QueryClient, atendenteId: string, estado: EstadoDePresenca, motivo?: string | null) {
  return escreverNaEquipe(cliente, `/api/painel/equipe/${encodeURIComponent(atendenteId)}/presenca`, {
    metodo: "PUT",
    corpo: { estado, ...(motivo ? { motivo } : {}) },
  });
}

/** As conversas abertas dela: de volta à fila (`"fila"`) ou para outra pessoa (o id). */
export function redistribuirConversas(cliente: QueryClient, atendenteId: string, para: string) {
  return escreverNaEquipe(cliente, `/api/painel/equipe/${encodeURIComponent(atendenteId)}/redistribuir`, {
    metodo: "POST",
    corpo: { para },
  });
}

export interface Perfil {
  readonly id: string;
  readonly rotulo: string;
  /** A frase diz o que o perfil NÃO faz. Vem do servidor. */
  readonly frase: string;
  readonly permissoes: readonly string[];
}

export interface Equipe {
  readonly itens: readonly AtendenteNaLista[];
  readonly perfis: readonly Perfil[];
  readonly horarioDoTenant: readonly (string | null)[] | null;
  readonly fuso: string;
  /** `false` enquanto o H9b não existir. A frase abaixo diz por quê, e vem do servidor. */
  readonly perfilEhSalvavel: boolean;
  readonly porQuePerfilNaoSalva: string;
  /**
   * Perfil de acesso por pessoa, vindo do Cert4All (H9b fechado em 2026-08-13).
   *
   * Chave é o `atendenteId` — o mesmo id que o resto desta tela usa. Vem vazia
   * quando quem está olhando não administra a empresa no Cert4All: aí a tela
   * mostra os perfis e não oferece o seletor.
   */
  readonly acessos: readonly AcessoDaPessoa[];
  /** Convites que ainda não foram aceitos, com o prazo de 72h correndo. */
  readonly convitesPendentes: readonly ConvitePendente[];
  /** O servidor decide: só quem administra a empresa no Cert4All convida. */
  readonly podeConvidar: boolean;
  /** Os motivos de pausa (H129). Opcional: servidor antigo não manda. */
  readonly motivosDePausa?: readonly string[];
}

export function useEquipe() {
  return useQuery({
    queryKey: ["equipe"],
    queryFn: () => chamar<Equipe>("/api/painel/equipe"),
  });
}

/** As quatro escritas de equipe, num lugar. Devolvem a falha para a tela mostrar a frase do servidor. */
export async function operarEquipe(
  cliente: QueryClient,
  atendenteId: string,
  acao: "salvar" | "desativar" | "reativar" | "liberar-hoje",
  corpo?: unknown,
): Promise<{ ok: boolean; dados?: Record<string, unknown>; falha?: FalhaDaApi }> {
  const caminho = `/api/painel/equipe/${encodeURIComponent(atendenteId)}`;
  try {
    const dados = await chamar<Record<string, unknown>>(
      acao === "salvar" ? caminho : acao === "desativar" ? caminho : `${caminho}/${acao}`,
      acao === "salvar"
        ? { metodo: "PATCH", corpo }
        : acao === "desativar"
          ? { metodo: "DELETE" }
          : { metodo: "POST", corpo: {} },
    );
    await cliente.invalidateQueries({ queryKey: ["equipe"] });
    // Desativar devolve atendimentos para a fila — a lista de conversas e o Início mudam.
    await cliente.invalidateQueries({ queryKey: ["atendimentos"] });
    await cliente.invalidateQueries({ queryKey: ["ativacao"] });
    return { ok: true, dados };
  } catch (erro) {
    const falha = (erro as { falha?: FalhaDaApi }).falha;
    return falha ? { ok: false, falha } : { ok: false };
  }
}

/**
 * Vincular e desvincular alguém de uma fila — o H69.
 *
 * ## Por que isto não existia
 *
 * `POST /filas/:id/atendentes` e o `DELETE` irmão existem desde o H31, com domínio e recorte de
 * tenant prontos, e **nenhum arquivo de `web/` os chamava**. Enquanto isso, `Equipe.tsx` mandava
 * *"Vincule em Ajustes → Filas"* — e lá só há criar e arquivar. A consequência não é cosmética: o
 * recorte do §7.2 entrega os `aguardando` a quem está **na fila**, então **sem fila o atendente não
 * vê conversa chegar**, que foi o bug que o H30 encontrou.
 *
 * ## A permissão é `manage_catalog`, e NÃO `manage_team`
 *
 * É a que a rota exige (`rotas.ts:847`), e gatear a tela por outra recriaria o defeito do "Convidar"
 * de 2026-08-16: botão vivo, servidor recusando. **E a diferença é real, não teórica** — o preset de
 * **Supervisor** tem `manage_catalog` e **não** tem `manage_team`. Distribuir gente entre
 * filas é trabalho de supervisor; mudar jornada e desativar pessoa é de administrador.
 *
 * ## `restrito` NÃO é enviado, e a omissão é deliberada
 *
 * A coluna existe na `0007` e o corpo da rota a aceita, mas **nada no servidor age sobre ela**: ela
 * é gravada, devolvida como `restritoNela` e nenhum `WHERE` a consulta. Oferecer o controle seria
 * publicar um interruptor que não faz nada — o defeito que este item existe para corrigir. Vai o
 * padrão (`false`), e a pendência está no BACKLOG.
 */
export async function vincularAFila(
  cliente: QueryClient,
  filaId: string,
  atendenteId: string,
): Promise<{ ok: boolean; falha?: FalhaDaApi }> {
  return await mexerNaFila(cliente, `/api/painel/filas/${encodeURIComponent(filaId)}/atendentes`, {
    metodo: "POST",
    corpo: { atendenteId },
  });
}

export async function desvincularDaFila(
  cliente: QueryClient,
  filaId: string,
  atendenteId: string,
): Promise<{ ok: boolean; falha?: FalhaDaApi }> {
  return await mexerNaFila(
    cliente,
    `/api/painel/filas/${encodeURIComponent(filaId)}/atendentes/${encodeURIComponent(atendenteId)}`,
    { metodo: "DELETE" },
  );
}

async function mexerNaFila(
  cliente: QueryClient,
  caminho: string,
  opcoes: { metodo: "POST" | "DELETE"; corpo?: unknown },
): Promise<{ ok: boolean; falha?: FalhaDaApi }> {
  try {
    await chamar(caminho, opcoes);
    await cliente.invalidateQueries({ queryKey: ["equipe"] });
    // As três que mudam de verdade, e cada uma por um motivo:
    // - `catalogos`: a lista de filas traz `minhas`, e ela acabou de mudar para quem se vinculou;
    // - `atendimentos`: o recorte do §7.2 é por fila — a lista de conversas muda **na hora**;
    // - `ativacao`: a checklist conta gente em `fila_atendentes`, e este é o passo "equipe".
    await cliente.invalidateQueries({ queryKey: chaves.catalogos() });
    await cliente.invalidateQueries({ queryKey: ["atendimentos"] });
    await cliente.invalidateQueries({ queryKey: ["ativacao"] });
    return { ok: true };
  } catch (erro) {
    const falha = (erro as { falha?: FalhaDaApi }).falha;
    return falha ? { ok: false, falha } : { ok: false };
  }
}

/**
 * O estado da jornada, calculado no navegador.
 *
 * Repetido do servidor (`estadoDaJornada` em `equipe.ts`) de propósito, e é a segunda duplicação que eu
 * aceito neste projeto — a primeira foi `preencherCorpo` do template. O motivo é o mesmo: o servidor
 * precisa dela para decidir, e a tela precisa dela para mostrar **ao vivo**, mudando de "fora" para "em
 * jornada" às oito da manhã sem esperar uma revalidação.
 *
 * O contrato entre as duas é o formato `HH:MM-HH:MM` e o índice do dia — nenhum dos dois é nosso: o
 * primeiro é convenção, o segundo é do `Intl`.
 */
export function estadoDaJornada(
  a: Pick<AtendenteNaLista, "jornada" | "liberadoAte" | "ativo">,
  horarioDoTenant: readonly (string | null)[] | null,
  fuso: string,
  agora = new Date(),
): "inativo" | "liberado" | "em jornada" | "fora" {
  if (!a.ativo) return "inativo";

  const hoje = new Intl.DateTimeFormat("en-CA", { timeZone: fuso }).format(agora);
  if (a.liberadoAte && a.liberadoAte >= hoje) return "liberado";

  const jornada = a.jornada ?? horarioDoTenant;
  if (!jornada) return "em jornada";

  const partes = new Map(
    new Intl.DateTimeFormat("en-US", {
      timeZone: fuso,
      weekday: "short",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    })
      .formatToParts(agora)
      .map((p) => [p.type, p.value]),
  );

  const indice = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].indexOf(partes.get("weekday") ?? "");
  const faixa = indice >= 0 ? jornada[indice] : null;
  if (!faixa) return "fora";

  const hora = (partes.get("hour") ?? "00").replace(/^24$/, "00");
  const agoraHhMm = `${hora}:${partes.get("minute") ?? "00"}`;
  const [inicio, fim] = faixa.split("-") as [string, string];
  return agoraHhMm >= inicio && agoraHhMm < fim ? "em jornada" : "fora";
}

// --- Widgets (§5.14) -----------------------------------------------------------------------------

export interface Widget {
  readonly id: string;
  readonly nome: string;
  readonly conexaoId: string | null;
  readonly mensagem: string;
  readonly filaId: string | null;
  readonly etiquetaId: string | null;
  readonly rotulo: string;
  readonly cor: string;
  readonly ativo: boolean;
  readonly criadoEm: string;
  readonly canal: string | null;
  readonly destino: string | null;
  /** Conversas que começaram com esta mensagem. Conversa, não clique — ver `widgets.ts`. */
  readonly conversas: number;
  /** Gerado no SERVIDOR: o escape de HTML e a forma da URL de cada canal são regra, não apresentação. */
  readonly trecho: { readonly html: string; readonly url: string | null; readonly aviso: string | null };
}

export function useWidgets() {
  return useQuery({
    queryKey: ["widgets"],
    queryFn: () => chamar<{ itens: Widget[] }>("/api/painel/widgets"),
  });
}

export async function criarWidget(
  cliente: QueryClient,
  corpo: Record<string, unknown>,
): Promise<{ ok: boolean; falha?: FalhaDaApi }> {
  try {
    await chamar("/api/painel/widgets", { metodo: "POST", corpo });
    await cliente.invalidateQueries({ queryKey: ["widgets"] });
    return { ok: true };
  } catch (erro) {
    const falha = (erro as { falha?: FalhaDaApi }).falha;
    return falha ? { ok: false, falha } : { ok: false };
  }
}

export async function arquivarWidget(cliente: QueryClient, id: string): Promise<void> {
  await chamar(`/api/painel/widgets/${id}`, { metodo: "DELETE" }).catch(() => null);
  await cliente.invalidateQueries({ queryKey: ["widgets"] });
}

// --- Agente Hugi (§5.15 da spec 04) ---------------------------------------------------------------

export type QuandoAtende = "nunca" | "fora_do_horario" | "sempre";

export interface ConfiguracaoDoAgente {
  readonly nome: string;
  readonly atende: QuandoAtende;
  readonly instrucoes: string | null;
  readonly maxTurnosSemHumano: number;
  readonly palavrasDeEscape: readonly string[];
  readonly filaDeEscalonamentoId: string | null;
  readonly etiquetaId: string | null;
  /** O que fazer com foto e com arquivo. Era código, e era a política de um negócio de imagem. */
  readonly midiaImagem: PoliticaDeMidia;
  readonly midiaArquivo: PoliticaDeMidia;
  readonly falaMidiaImagem: string | null;
  readonly falaMidiaArquivo: string | null;
  readonly falaArquivoNaoLido: string | null;
  readonly atualizadoEm: string | null;
  readonly atualizadoPor: string | null;
}

export type PoliticaDeMidia = "transbordar" | "evasiva" | "ignorar";

/**
 * Uma REGRA DE TRANSBORDO do assistente (`agente_regras`, 0075).
 *
 * *Quando a mensagem contiver isto, passe para uma pessoa e diga aquilo.* Era `sinais.ts` — cinco
 * listas de fotografia em código, valendo para todo tenant e forçando transbordo.
 */
export interface RegraDoAgente {
  readonly id: string;
  readonly codigo: string;
  readonly rotulo: string;
  readonly termos: readonly string[];
  readonly ordem: number;
  readonly fala: string | null;
  readonly ativa: boolean;
  readonly deFabrica: boolean;
}

/**
 * O estado, com os **três** impedimentos separados.
 *
 * `operante` sozinho não serve para a tela: ela precisa dizer *quem resolve* cada coisa que falta — nós
 * (a chave do provedor), o cliente (as instruções) ou ninguém (a janela do canal).
 */
export interface EstadoDoAgente {
  readonly operante: boolean;
  readonly provedor: { readonly configurado: boolean; readonly nome: string; readonly comoConfigurar: string | null };
  readonly configuracao: { readonly ligado: boolean; readonly resumo: string };
  readonly canais: readonly { readonly canal: string; readonly rotulo: string; readonly aviso: string }[];
  readonly decisoes: readonly {
    readonly id: string;
    readonly atendimentoId: string;
    readonly decisao: "respondeu" | "passou_para_humano" | "recusou";
    readonly motivo: string | null;
    readonly turno: number;
    readonly criadoEm: string;
  }[];
  readonly contagem: { readonly respondeu: number; readonly passou: number; readonly recusou: number };
}

export interface Procedimento {
  readonly id: string;
  readonly nome: string;
  readonly quandoUsar: string;
  readonly instrucoes: string;
  readonly ativo: boolean;
}

export interface TarefaDoAgente {
  readonly id: string;
  readonly nome: string;
  readonly quandoUsar: string;
  readonly operacao: string;
  readonly filaId: string | null;
  readonly motivoId: string | null;
  readonly etiquetaId: string | null;
  readonly exigeConfirmacao: boolean;
  readonly ativa: boolean;
}

/** Um agente da empresa, como aparece no seletor da tela e no passo do fluxo (0057). */
export interface ResumoDoAgente {
  readonly id: string;
  readonly nome: string;
  readonly atende: QuandoAtende;
  readonly temInstrucoes: boolean;
  readonly versao: number;
  readonly publicacoes: number;
  readonly principal: boolean;
  readonly criadoEm: string;
}

/** A lista leve, para quem só precisa escolher um (o passo do fluxo). */
export function useAgentes() {
  return useQuery({
    queryKey: ["agentes"],
    queryFn: () => chamar<{ agentes: ResumoDoAgente[] }>("/api/painel/agentes"),
  });
}

async function invalidarAgentes(cliente: QueryClient): Promise<void> {
  await Promise.all([
    cliente.invalidateQueries({ queryKey: ["agente"] }),
    cliente.invalidateQueries({ queryKey: ["agentes"] }),
    cliente.invalidateQueries({ queryKey: ["automacoes"] }),
  ]);
}

export async function criarAgente(
  cliente: QueryClient,
  corpo: { nome: string },
): Promise<{ ok: boolean; id?: string; falha?: FalhaDaApi }> {
  try {
    const r = await chamar<{ id?: string }>("/api/painel/agentes", { metodo: "POST", corpo });
    await invalidarAgentes(cliente);
    return { ok: true, ...(r?.id ? { id: r.id } : {}) };
  } catch (erro) {
    const falha = (erro as { falha?: FalhaDaApi }).falha;
    return falha ? { ok: false, falha } : { ok: false };
  }
}

export async function arquivarAgente(cliente: QueryClient, id: string): Promise<{ ok: boolean; falha?: FalhaDaApi }> {
  try {
    await chamar(`/api/painel/agentes/${id}`, { metodo: "DELETE" });
    await invalidarAgentes(cliente);
    return { ok: true };
  } catch (erro) {
    const falha = (erro as { falha?: FalhaDaApi }).falha;
    return falha ? { ok: false, falha } : { ok: false };
  }
}

/** `?agente=` vazio cai no principal — é o servidor quem decide qual é. */
const comAgente = (url: string, agenteId: string | null): string =>
  agenteId ? `${url}${url.includes("?") ? "&" : "?"}agente=${encodeURIComponent(agenteId)}` : url;

export function useAgente(agenteId: string | null = null) {
  return useQuery({
    queryKey: ["agente", agenteId],
    queryFn: () =>
      chamar<{
        agentes: ResumoDoAgente[];
        agente: ResumoDoAgente | null;
        configuracao: ConfiguracaoDoAgente;
        estado: EstadoDoAgente;
        filas: { id: string; nome: string }[];
        etiquetas: { id: string; nome: string; cor: string | null }[];
        diretrizes: Diretriz[];
        conhecimento: Conhecimento[];
        simulacoes: Simulacao[];
        publicacoes: PublicacaoDoAgente[];
        /** Vocabulário de produto: cada tipo diz qual pergunta responde. Vem do servidor. */
        tiposDeDiretriz: { id: string; rotulo: string; pergunta: string }[];
        /** Cada expectativa diz qual DEFEITO ela pega. É o que faz a pessoa escolher a certa. */
        esperas: { id: string; rotulo: string; pega: string }[];
        /** Procedimentos e tarefas (§4 da spec 08). */
        procedimentos: Procedimento[];
        regras: RegraDoAgente[];
        tarefas: TarefaDoAgente[];
        /** A lista é FECHADA: são as operações do atendente. Vem do servidor com a frase de cada uma. */
        operacoesDeTarefa: {
          id: string;
          rotulo: string;
          precisa: "fila" | "motivo" | "etiqueta" | null;
          frase: string;
        }[];
        motivos: { id: string; nome: string }[];
      }>(comAgente("/api/painel/agente", agenteId)),
  });
}

/**
 * `PATCH` com o corpo COMPLETO — mesma escolha de `salvarConfiguracoes`, e a mesma razão: o servidor
 * substitui o registro inteiro, porque "apagou as frases de escape" e "não mencionou as frases de escape"
 * precisam ser distinguíveis. O verbo segue a convenção do resto do painel; a semântica é de substituição.
 */
export async function salvarRegraDoAgente(
  cliente: QueryClient,
  agenteId: string | null,
  corpo: Record<string, unknown>,
): Promise<{ ok: boolean; falha?: FalhaDaApi }> {
  try {
    await chamar(comAgente("/api/painel/agente/regras", agenteId), { metodo: "POST", corpo });
    await invalidarAgentes(cliente);
    return { ok: true };
  } catch (erro) {
    const falha = (erro as { falha?: FalhaDaApi }).falha;
    return falha ? { ok: false, falha } : { ok: false };
  }
}

export async function apagarRegraDoAgente(
  cliente: QueryClient,
  agenteId: string | null,
  id: string,
): Promise<{ ok: boolean; falha?: FalhaDaApi }> {
  try {
    await chamar(comAgente(`/api/painel/agente/regras/${id}`, agenteId), { metodo: "DELETE" });
    await invalidarAgentes(cliente);
    return { ok: true };
  } catch (erro) {
    const falha = (erro as { falha?: FalhaDaApi }).falha;
    return falha ? { ok: false, falha } : { ok: false };
  }
}

export async function salvarAgente(
  cliente: QueryClient,
  agenteId: string | null,
  corpo: Record<string, unknown>,
): Promise<{ ok: boolean; falha?: FalhaDaApi }> {
  try {
    await chamar(comAgente("/api/painel/agente", agenteId), { metodo: "PATCH", corpo });
    await invalidarAgentes(cliente);
    return { ok: true };
  } catch (erro) {
    const falha = (erro as { falha?: FalhaDaApi }).falha;
    return falha ? { ok: false, falha } : { ok: false };
  }
}

// --- Agenda e lembretes (§5.17 da spec 04) --------------------------------------------------------

export interface ItemDaAgenda {
  readonly id: string;
  readonly tipo: "mensagem" | "lembrete";
  readonly quando: string;
  readonly texto: string;
  readonly estado: "pendente" | "feito" | "falhou" | "cancelado";
  /** Por que não saiu. Coluna, não log — é o que responde "por que meu cliente não recebeu". */
  readonly erro: string | null;
  readonly atendimentoId: string | null;
  readonly protocolo: string | null;
  readonly contatoId: string | null;
  readonly contatoNome: string | null;
  readonly templateNome: string | null;
  readonly paraAtendenteId: string | null;
  readonly criadoPor: string | null;
  readonly criadoEm: string;
  readonly concluidoEm: string | null;
}

/**
 * `janelaHoras` vem do servidor porque é regra do canal (mora em `src/canal/catalogo.ts`).
 *
 * A FRASE que a tela mostra é da tela: mandar o texto pronto faria o servidor decidir microcópia, e
 * mandar nada faria a tela ter a segunda cópia do número de horas.
 */
export interface CanalDaAgenda {
  readonly canal: string;
  readonly modo: string;
  readonly nome: string;
  readonly janelaHoras: number | null;
}

export interface ConversaParaAgendar {
  readonly atendimentoId: string;
  readonly protocolo: string;
  readonly contatoNome: string | null;
  readonly canal: string;
  readonly modo: string;
  readonly estado: string;
}

export function useAgenda() {
  return useQuery({
    queryKey: ["agenda"],
    queryFn: () =>
      chamar<{
        itens: ItemDaAgenda[];
        canais: CanalDaAgenda[];
        conversas: ConversaParaAgendar[];
      }>("/api/painel/agenda"),
  });
}

export async function criarAgendamento(
  cliente: QueryClient,
  corpo: Record<string, unknown>,
): Promise<{ ok: boolean; falha?: FalhaDaApi }> {
  try {
    await chamar("/api/painel/agenda", { metodo: "POST", corpo });
    await cliente.invalidateQueries({ queryKey: ["agenda"] });
    return { ok: true };
  } catch (erro) {
    const falha = (erro as { falha?: FalhaDaApi }).falha;
    return falha ? { ok: false, falha } : { ok: false };
  }
}

export async function cancelarAgendamento(cliente: QueryClient, id: string): Promise<void> {
  await chamar(`/api/painel/agenda/${id}`, { metodo: "DELETE" }).catch(() => null);
  await cliente.invalidateQueries({ queryKey: ["agenda"] });
}

/** "Feito" no lembrete pendente — o agendador não conclui sozinho (ele não sabe se alguém ligou). */
export async function concluirAgendamento(cliente: QueryClient, id: string): Promise<void> {
  await chamar(`/api/painel/agenda/${id}/concluir`, { metodo: "POST" }).catch(() => null);
  await cliente.invalidateQueries({ queryKey: ["agenda"] });
}

// --- Chat interno (§5.18 da spec 04) --------------------------------------------------------------

export interface ConversaInterna {
  readonly comQuem: string;
  readonly ultimaEm: string;
  readonly naoLidas: number;
  readonly previa: string;
  readonly minhaFoiAUltima: boolean;
}

export interface MensagemInterna {
  readonly id: string;
  readonly deId: string;
  readonly paraId: string;
  readonly texto: string;
  /** Quando existe, a tela vira isto num LINK para a conversa. É o motivo do chat existir. */
  readonly atendimentoCitadoId: string | null;
  readonly protocoloCitado: string | null;
  readonly lidoEm: string | null;
  readonly criadoEm: string;
}

export function useChat() {
  return useQuery({
    queryKey: ["chat"],
    queryFn: () =>
      chamar<{
        conversas: ConversaInterna[];
        naoLidasTotal: number;
        eu: string;
        pessoas: { id: string; nome: string | null }[];
        /** Se ESTA pessoa pode começar conversa nova (0058). Responder é sempre livre. */
        podeIniciar: boolean;
      }>("/api/painel/chat"),
  });
}

/**
 * O fio com uma pessoa.
 *
 * **Abrir é ler**: a rota marca como lido, e por isso este hook invalida a caixa quando termina — sem isso
 * o contador de não lidas continuaria aceso na lista ao lado do fio já aberto.
 */
export function useFioInterno(comQuem: string) {
  const cliente = useQueryClient();
  return useQuery({
    queryKey: ["chat", comQuem],
    queryFn: async () => {
      const r = await chamar<{ itens: MensagemInterna[] }>(
        `/api/painel/chat/${encodeURIComponent(comQuem)}`,
      );
      await cliente.invalidateQueries({ queryKey: ["chat"], exact: true });
      return r;
    },
  });
}

export async function enviarInterna(
  cliente: QueryClient,
  corpo: Record<string, unknown>,
): Promise<{ ok: boolean; falha?: FalhaDaApi }> {
  try {
    await chamar("/api/painel/chat", { metodo: "POST", corpo });
    await cliente.invalidateQueries({ queryKey: ["chat"] });
    return { ok: true };
  } catch (erro) {
    const falha = (erro as { falha?: FalhaDaApi }).falha;
    return falha ? { ok: false, falha } : { ok: false };
  }
}

// --- Fluxo de triagem (§5.19 da spec 04) ----------------------------------------------------------

/**
 * Os tipos do fluxo vêm de `src/canal/fluxo.ts`, e não são redeclarados aqui.
 *
 * Aquele módulo é puro — nenhum import, nenhum banco, nenhuma API de Node — e é por isso que dá para
 * trazê-lo para o navegador. Redeclarar as formas aqui produziria dois formatos de passo que divergem na
 * primeira mudança, e a divergência apareceria como fluxo salvo que a automação não entende.
 */
export type {
  Fluxo,
  PassoDoFluxo,
  OpcaoDoPasso,
  PassoCompleto,
  TipoDePasso,
  Gatilho as TipoDeGatilho,
} from "../../../src/canal/fluxo.js";

/** As três guardas do disparo proativo (§3.2 da spec 11), com o estado real do tenant. */
export interface ProativoDoTenant {
  /** `null` quando não há fluxo de blocos publicado — e aí a tela diz isso, não "não pode". */
  readonly podeAbordar: boolean | null;
  readonly porQueNaoAborda: string | null;
  readonly contatosComOptin: number;
  readonly contatosSemOptin: number;
  readonly categoriasDeTemplate: readonly { categoria: string; quantos: number }[];
}

export interface UsoDoFluxo {
  readonly emAndamento: number;
  readonly terminados: number;
  /** Só no motor de blocos: estourar teto é o único desfecho que exige alguém olhar. */
  readonly estourados?: number;
  /**
   * De onde estes números vieram — `fluxo_estado` (menu) ou `execucoes_de_fluxo` (blocos).
   *
   * A tela escreve a diferença. Sem isto ela dizia "passaram pelo menu" num fluxo de blocos, e mostrava
   * execuções antigas de menu embaixo dele: dado real, da coisa errada.
   */
  readonly deBlocos: boolean;
  readonly ultimos: readonly {
    readonly atendimentoId: string;
    readonly protocolo: string | null;
    readonly passoAtual: string | null;
    readonly terminado: boolean;
    readonly terminouPorque: string | null;
    readonly em: string;
  }[];
}

export type {
  FormaSimples,
  ProblemaDoFluxo,
} from "../../../src/canal/fluxo.js";

/** Um fluxo da empresa, como aparece na lista (0061). */
export interface ResumoDoFluxo {
  readonly id: string;
  readonly nome: string;
  readonly quando: import("../../../src/canal/fluxo.js").Fluxo["quando"];
  readonly quandoValor: string | null;
  readonly versao: number | null;
  readonly temRascunho: boolean;
  readonly publicadoEm: string | null;
  readonly criadoEm: string;
  readonly atualizadoEm: string | null;
}

export interface RespostaDoFluxo {
  /** Todos os fluxos vivos da empresa — a lista de onde se escolhe qual editar. */
  readonly fluxos: ResumoDoFluxo[];
  readonly fluxo:
    | (import("../../../src/canal/fluxo.js").Fluxo & {
        atualizadoEm: string | null;
        atualizadoPor: string | null;
        rascunho: import("../../../src/canal/fluxo.js").PassoDoFluxo[] | null;
        rascunhoEm: string | null;
        rascunhoPor: string | null;
        versao: number | null;
        publicadoEm: string | null;
        publicadoPor: string | null;
      })
    | null;
  /** O fluxo sugerido, para quem nunca montou nada. **Não** está gravado. */
  readonly exemplo: import("../../../src/canal/fluxo.js").PassoDoFluxo[];
  /** O que o editor edita: o rascunho quando existe, senão o publicado. Decidido no servidor. */
  readonly emEdicao: import("../../../src/canal/fluxo.js").PassoDoFluxo[];
  readonly problemas: import("../../../src/canal/fluxo.js").ProblemaDoFluxo[];
  /** `null` quando o fluxo não cabe no degrau simples — e aí `porqueNaoCabeNoSimples` explica. */
  readonly simples: import("../../../src/canal/fluxo.js").FormaSimples | null;
  readonly porqueNaoCabeNoSimples: string | null;
  readonly diferenca: {
    adicionados: string[];
    removidos: string[];
    alterados: { passo: string; oQue: string[] }[];
    semMudanca: boolean;
  } | null;
  readonly versoes: {
    versao: number;
    publicadoEm: string;
    publicadoPor: string | null;
    nota: string | null;
  }[];
  readonly uso: UsoDoFluxo;
  /** `null` quando a empresa ainda não tem fluxo nenhum. */
  readonly proativo: ProativoDoTenant | null;
  readonly filas: { id: string; nome: string }[];
  readonly etiquetas: { id: string; nome: string }[];
  /**
   * A galeria. São **constantes de código**, não linhas de banco — ver `MODELOS` em `src/canal/fluxo.ts`.
   *
   * É a decisão de segurança do §8b da spec 11: galeria é a tela que se esquece de recortar por tenant, e
   * a nossa não corre o risco porque não há nada de ninguém dentro dela.
   */
  readonly modelos: {
    id: string;
    nome: string;
    paraQuem: string;
    limite: string | null;
    passos: import("../../../src/canal/fluxo.js").PassoDoFluxo[];
  }[];
}

const comFluxo = (url: string, fluxoId: string | null): string =>
  fluxoId ? `${url}?fluxo=${encodeURIComponent(fluxoId)}` : url;

/** `null` = o primeiro da lista (o que atende) — é o que o Início usa. */
export function useFluxo(fluxoId: string | null = null) {
  return useQuery({
    queryKey: ["fluxo", fluxoId],
    queryFn: () => chamar<RespostaDoFluxo>(comFluxo("/api/painel/fluxo", fluxoId)),
  });
}
export function useFluxos() {
  return useQuery({
    queryKey: ["fluxos"],
    queryFn: () => chamar<{ fluxos: ResumoDoFluxo[] }>("/api/painel/fluxos"),
  });
}
async function invalidarFluxos(cliente: QueryClient): Promise<void> {
  await Promise.all([
    cliente.invalidateQueries({ queryKey: ["fluxo"] }),
    cliente.invalidateQueries({ queryKey: ["fluxos"] }),
    // A lista de Automações mostra o gatilho, a forma e o quanto rodou — criar, publicar ou arquivar
    // muda as três. Sem esta linha, a tela volta da edição mostrando o estado anterior.
    cliente.invalidateQueries({ queryKey: ["automacoes"] }),
  ]);
}

/* --- AUTOMAÇÕES: a leitura da área única ------------------------------------------------------ */

export type FormaDaAutomacao = "menu" | "roteiro" | "vazia";

export interface Automacao extends ResumoDoFluxo {
  readonly forma: FormaDaAutomacao;
  readonly resumo: string;
  readonly rodou30d: number;
  readonly assistentes: readonly string[];
}

export interface Assistente extends ResumoDoAgente {
  readonly usadoPor: readonly { readonly id: string; readonly nome: string }[];
}

export function useAutomacoes() {
  return useQuery({
    queryKey: ["automacoes"],
    queryFn: () => chamar<{ automacoes: Automacao[]; assistentes: Assistente[] }>("/api/painel/automacoes"),
  });
}
/* --- MODELOS: a biblioteca, importar e exportar ---------------------------------------------- */

export interface ModeloDaBiblioteca {
  id: string;
  nome: string;
  setor: string;
  descricao: string;
  temFluxo: boolean;
  temAgente: boolean;
  passos: number;
}

export function useModelos() {
  return useQuery({
    queryKey: ["modelos"],
    queryFn: () => chamar<{ modelos: ModeloDaBiblioteca[] }>("/api/painel/modelos"),
  });
}

export interface ResultadoDaImportacao {
  fluxoId?: string | null;
  agenteId?: string | null;
  criados?: { tipo: string; nome: string }[];
  pendencias?: string[];
}

export async function importarModelo(
  cliente: QueryClient,
  corpo: { deFabrica?: string; modelo?: unknown; nomeDoFluxo?: string; nomeDoAgente?: string },
): Promise<{ ok: boolean; dados?: ResultadoDaImportacao; falha?: FalhaDaApi }> {
  try {
    const r = await chamar<ResultadoDaImportacao>("/api/painel/modelos/importar", { metodo: "POST", corpo });
    await invalidarFluxos(cliente);
    await cliente.invalidateQueries({ queryKey: ["agentes"] });
    await cliente.invalidateQueries({ queryKey: ["catalogos"] });
    return { ok: true, ...(r ? { dados: r } : {}) };
  } catch (erro) {
    const falha = (erro as { falha?: FalhaDaApi }).falha;
    return falha ? { ok: false, falha } : { ok: false };
  }
}

/** O endereço do arquivo. A tela navega para ele — baixar é do navegador, não do nosso código. */
export function enderecoDeExportacao(o: { fluxoId?: string | null; agenteId?: string | null; conhecimento?: boolean }): string {
  const q = new URLSearchParams();
  if (o.fluxoId) q.set("fluxo", o.fluxoId);
  if (o.agenteId) q.set("agente", o.agenteId);
  if (o.conhecimento === false) q.set("conhecimento", "nao");
  return `/api/painel/modelos/exportar?${q.toString()}`;
}

export async function criarFluxo(
  cliente: QueryClient,
  corpo: { nome: string; passos?: unknown },
): Promise<{ ok: boolean; id?: string; falha?: FalhaDaApi }> {
  try {
    const r = await chamar<{ id?: string }>("/api/painel/fluxos", { metodo: "POST", corpo });
    await invalidarFluxos(cliente);
    return { ok: true, ...(r?.id ? { id: r.id } : {}) };
  } catch (erro) {
    const falha = (erro as { falha?: FalhaDaApi }).falha;
    return falha ? { ok: false, falha } : { ok: false };
  }
}
export async function arquivarFluxo(cliente: QueryClient, id: string): Promise<{ ok: boolean; falha?: FalhaDaApi }> {
  try {
    await chamar(`/api/painel/fluxos/${id}`, { metodo: "DELETE" });
    await invalidarFluxos(cliente);
    return { ok: true };
  } catch (erro) {
    const falha = (erro as { falha?: FalhaDaApi }).falha;
    return falha ? { ok: false, falha } : { ok: false };
  }
}

/** Publicar. Devolve os problemas quando recusa — a tela mostra a lista, não uma frase genérica. */
export async function publicarFluxo(
  cliente: QueryClient,
  fluxoId: string,
  corpo: Record<string, unknown>,
): Promise<{ ok: boolean; falha?: FalhaDaApi }> {
  try {
    await chamar(comFluxo("/api/painel/fluxo/publicar", fluxoId), { metodo: "POST", corpo });
    await invalidarFluxos(cliente);
    return { ok: true };
  } catch (erro) {
    const falha = (erro as { falha?: FalhaDaApi }).falha;
    return falha ? { ok: false, falha } : { ok: false };
  }
}

export async function descartarRascunho(cliente: QueryClient, fluxoId: string): Promise<void> {
  await chamar(comFluxo("/api/painel/fluxo/rascunho", fluxoId), { metodo: "DELETE" }).catch(() => null);
  await invalidarFluxos(cliente);
}

export async function salvarFluxo(
  cliente: QueryClient,
  fluxoId: string,
  corpo: Record<string, unknown>,
): Promise<{ ok: boolean; falha?: FalhaDaApi }> {
  try {
    await chamar(comFluxo("/api/painel/fluxo", fluxoId), { metodo: "PATCH", corpo });
    await invalidarFluxos(cliente);
    return { ok: true };
  } catch (erro) {
    const falha = (erro as { falha?: FalhaDaApi }).falha;
    return falha ? { ok: false, falha } : { ok: false };
  }
}

// --- Importar contatos (§5.20 da spec 04) ---------------------------------------------------------

export interface LinhaRecusada {
  readonly linha: number;
  readonly conteudo: string;
  readonly problema: string;
  readonly explicacao: string;
}

export interface ConferenciaDaImportacao {
  /** As dez primeiras linhas INTERPRETADAS. É o que mostra se as colunas foram lidas certo. */
  readonly amostra: readonly { linha: number; /** @cru */ identificador: string; nome: string | null; etiquetas: string[] }[];
  readonly quantasAceitas: number;
  readonly recusadas: readonly LinhaRecusada[];
  readonly quantasRecusadas: number;
  readonly etiquetas: readonly string[];
}

/**
 * Confere **sem gravar**.
 *
 * Não é `useQuery` porque não é leitura de estado: é uma pergunta sobre um texto que a pessoa acabou de
 * colar, e cachear a resposta por chave faria a segunda tentativa mostrar o resultado da primeira.
 */
export async function conferirImportacao(csv: string, canal: string): Promise<ConferenciaDaImportacao> {
  return chamar<ConferenciaDaImportacao>("/api/painel/contatos/conferir", {
    metodo: "POST",
    corpo: { csv, canal },
  });
}

export async function importarContatos(
  cliente: QueryClient,
  csv: string,
  canal: string,
  comProva = false,
): Promise<
  | { ok: true; criados: number; atualizados: number; etiquetasCriadas: number; optinsRegistrados: number; recusadas: LinhaRecusada[] }
  | { ok: false; falha?: FalhaDaApi }
> {
  try {
    const r = await chamar<{
      criados: number;
      atualizados: number;
      etiquetasCriadas: number;
      optinsRegistrados: number;
      recusadas: LinhaRecusada[];
    }>("/api/painel/contatos/importar", { metodo: "POST", corpo: { csv, canal, comProva } });
    await cliente.invalidateQueries({ queryKey: ["contatos"] });
    await cliente.invalidateQueries({ queryKey: ["catalogos"] });
    return { ok: true, ...r };
  } catch (erro) {
    const falha = (erro as { falha?: FalhaDaApi }).falha;
    return falha ? { ok: false, falha } : { ok: false };
  }
}

// --- Relatórios da spec 09 ------------------------------------------------------------------------

export interface Backlog {
  readonly dias: readonly { dia: string; abertas: number; fechadas: number; saldo: number }[];
  readonly abertasNoPeriodo: number;
  readonly fechadasNoPeriodo: number;
  readonly saldoNoPeriodo: number;
  /** Dias seguidos, contando de hoje para trás, com saldo positivo. É o sinal de afogamento. */
  readonly diasSeguidosPositivos: number;
  readonly abertasAgora: number;
}

export function useBacklog(dias: number) {
  return useQuery({
    queryKey: ["relatorio", "backlog", dias],
    queryFn: () => chamar<Backlog>(`/api/painel/relatorios/backlog?dias=${dias}`),
  });
}

export interface AgenteDeIa {
  readonly atendimentosTocados: number;
  readonly respostas: number;
  readonly resolvidasSemHumano: number;
  /** Passou DEPOIS de conversar. É sucesso: a pessoa recebeu a conversa pronta. */
  readonly escalonadasComTriagem: number;
  /** Largou na primeira mensagem. Aponta buraco nas instruções. */
  readonly escalonadasSemTriagem: number;
  readonly recusas: readonly { motivo: string; quantas: number }[];
  readonly fluxo: {
    readonly conversasNoMenu: number;
    readonly encaminhadasPeloMenu: number;
    readonly desistenciasDoMenu: number;
  };
  /** `null` até as tarefas do agente existirem (spec 08 §4). A tela diz o que falta. */
  /** `null` só quando o cliente não cadastrou nenhuma tarefa — ver o servidor. */
  readonly objetivosConcluidos: number | null;
  readonly tarefasRecusadas: number;
  readonly tarefasAguardando: number;
}

export function useAgenteDeIa(dias: number) {
  return useQuery({
    queryKey: ["relatorio", "agente", dias],
    queryFn: () => chamar<AgenteDeIa>(`/api/painel/relatorios/agente?dias=${dias}`),
  });
}

export interface Anuncios {
  readonly itens: readonly {
    anuncioId: string | null;
    titulo: string | null;
    tipoDeOrigem: string | null;
    conversas: number;
    contatos: number;
    finalizadas: number;
  }[];
  readonly conversasComAnuncio: number;
  readonly conversasTotais: number;
}

export function useAnuncios(dias: number) {
  return useQuery({
    queryKey: ["relatorio", "anuncios", dias],
    queryFn: () => chamar<Anuncios>(`/api/painel/relatorios/anuncios?dias=${dias}`),
  });
}

// --- O agente como produto (spec 08) -------------------------------------------------------------

export interface Diretriz {
  readonly id: string;
  readonly tipo: "uso_de_conteudo" | "esclarecimento" | "estilo" | "escalonamento";
  readonly titulo: string;
  readonly corpo: string;
  readonly ativa: boolean;
  readonly ordem: number;
  /** As três básicas. Podem ser desligadas e reescritas; não podem ser apagadas. */
  readonly deFabrica: boolean;
}

export interface Conhecimento {
  readonly id: string;
  readonly titulo: string;
  readonly estado: "recebido" | "extraido" | "indexado" | "falhou";
  readonly motivoDaFalha: string | null;
  /** `true` quando o Hugi gerou a partir do cadastro. A tela diz isso, em vez de fingir upload. */
  readonly geradoPeloHugi: boolean;
  readonly caracteres: number;
  readonly criadoEm: string;
  readonly processadoEm: string | null;
}

export interface Simulacao {
  readonly id: string;
  readonly titulo: string;
  readonly mensagens: string[];
  readonly espera: "responde_com" | "escala" | "nao_afirma" | "chama_tarefa";
  readonly esperaTexto: string | null;
  readonly ativa: boolean;
  readonly ultimoResultado: "passou" | "falhou" | "nao_rodou" | null;
  readonly ultimoDetalhe: string | null;
  readonly rodadaEm: string | null;
  /** O agente mudou depois da última rodada — o resultado em tela está velho. */
  readonly desatualizada: boolean;
}

export interface PublicacaoDoAgente {
  readonly agenteId: string | null;
  readonly agenteNome: string | null;
  readonly conexaoId: string;
  readonly conexaoRotulo: string;
  readonly versao: number;
  readonly publicadoEm: string;
  readonly publicadoPor: string | null;
}

export async function salvarDiretriz(cliente: QueryClient, agenteId: string | null, corpo: Record<string, unknown>) {
  return escreverNoAgente(cliente, agenteId, "/api/painel/agente/diretrizes", "POST", corpo);
}
export async function apagarDiretriz(cliente: QueryClient, agenteId: string | null, id: string) {
  return escreverNoAgente(cliente, agenteId, `/api/painel/agente/diretrizes/${id}`, "DELETE");
}
export async function acrescentarConhecimento(cliente: QueryClient, agenteId: string | null, corpo: Record<string, unknown>) {
  return escreverNoAgente(cliente, agenteId, "/api/painel/agente/conhecimento", "POST", corpo);
}
export async function apagarConhecimento(cliente: QueryClient, agenteId: string | null, id: string) {
  return escreverNoAgente(cliente, agenteId, `/api/painel/agente/conhecimento/${id}`, "DELETE");
}
export interface ParDeConhecimento {
  readonly pergunta: string;
  readonly resposta: string;
}
/**
 * Pede à IA pares pergunta/resposta de um material pronto (H78). NÃO grava — nem invalida nada: quem
 * grava é o dono, depois de conferir, pelo `acrescentarConhecimento` de sempre.
 */
export async function gerarParesDoConhecimento(
  agenteId: string | null,
  id: string,
): Promise<{ ok: true; pares: readonly ParDeConhecimento[]; cortado: boolean } | { ok: false; mensagem: string }> {
  try {
    const r = await chamar<{ pares: readonly ParDeConhecimento[]; cortado: boolean }>(
      comAgente(`/api/painel/agente/conhecimento/${id}/pares`, agenteId),
      { metodo: "POST" },
    );
    return { ok: true, pares: r.pares, cortado: r.cortado };
  } catch (erro) {
    const falha = (erro as { falha?: FalhaDaApi }).falha;
    return { ok: false, mensagem: falha?.erro.mensagem ?? "Não foi possível gerar agora." };
  }
}
export async function salvarProcedimento(cliente: QueryClient, agenteId: string | null, corpo: Record<string, unknown>) {
  return escreverNoAgente(cliente, agenteId, "/api/painel/agente/procedimentos", "POST", corpo);
}
export async function apagarProcedimento(cliente: QueryClient, agenteId: string | null, id: string) {
  return escreverNoAgente(cliente, agenteId, `/api/painel/agente/procedimentos/${id}`, "DELETE");
}
export async function salvarTarefa(cliente: QueryClient, agenteId: string | null, corpo: Record<string, unknown>) {
  return escreverNoAgente(cliente, agenteId, "/api/painel/agente/tarefas", "POST", corpo);
}
export async function apagarTarefa(cliente: QueryClient, agenteId: string | null, id: string) {
  return escreverNoAgente(cliente, agenteId, `/api/painel/agente/tarefas/${id}`, "DELETE");
}
export async function salvarSimulacao(cliente: QueryClient, agenteId: string | null, corpo: Record<string, unknown>) {
  return escreverNoAgente(cliente, agenteId, "/api/painel/agente/simulacoes", "POST", corpo);
}
export async function apagarSimulacao(cliente: QueryClient, agenteId: string | null, id: string) {
  return escreverNoAgente(cliente, agenteId, `/api/painel/agente/simulacoes/${id}`, "DELETE");
}
export async function rodarSimulacoes(cliente: QueryClient, agenteId: string | null) {
  return escreverNoAgente(cliente, agenteId, "/api/painel/agente/simulacoes/rodar", "POST", {});
}
export async function publicarAgente(cliente: QueryClient, agenteId: string | null, corpo: Record<string, unknown>) {
  return escreverNoAgente(cliente, agenteId, "/api/painel/agente/publicar", "POST", corpo);
}
export async function despublicarAgente(cliente: QueryClient, agenteId: string | null, conexaoId: string) {
  return escreverNoAgente(cliente, agenteId, `/api/painel/agente/publicar/${conexaoId}`, "DELETE");
}

/**
 * Um escritor só para as nove rotas do agente.
 *
 * Nove funções idênticas exceto pela URL seriam nove lugares para esquecer o `invalidateQueries` — e a tela
 * que não invalida mostra o estado anterior depois de uma ação que deu certo, que é o defeito mais confuso
 * de reproduzir.
 */
async function escreverNoAgente(
  cliente: QueryClient,
  agenteId: string | null,
  url: string,
  metodo: "POST" | "DELETE",
  corpo?: Record<string, unknown>,
): Promise<{ ok: boolean; falha?: FalhaDaApi }> {
  try {
    await chamar(comAgente(url, agenteId), { metodo, ...(corpo ? { corpo } : {}) });
    await invalidarAgentes(cliente);
    return { ok: true };
  } catch (erro) {
    const falha = (erro as { falha?: FalhaDaApi }).falha;
    return falha ? { ok: false, falha } : { ok: false };
  }
}

// --- SLA (spec 09 §3 e §5) ------------------------------------------------------------------------

export interface PoliticaDeSla {
  readonly id: string;
  readonly nome: string;
  readonly metaPrimeiraRespostaMin: number | null;
  readonly metaResolucaoMin: number | null;
  /** Pausa o relógio fora do horário de atendimento. Nasce desligado — ver o §3.3. */
  readonly contaApenasEmHorario: boolean;
  /** Se a resposta do assistente conta como primeira resposta. Padrão `false`, e o motivo é forte. */
  readonly agenteContaComoResposta: boolean;
  readonly filaId: string | null;
  readonly canal: string | null;
  readonly ativa: boolean;
  readonly filaNome: string | null;
}

export interface RelatorioDeSla {
  readonly medidos: number;
  readonly cumpridasPrimeiraResposta: number;
  readonly excedidasPrimeiraResposta: number;
  readonly cumpridasResolucao: number;
  readonly excedidasResolucao: number;
  /** `null` quando não houve nenhum medido — e `null` não é 0%, que diria "nada cumpriu". */
  readonly taxaPrimeiraResposta: number | null;
  readonly taxaResolucao: number | null;
  /** O custo declarado da pausa, em número. A pausa tem de ser visível. */
  readonly segundosEmEsperaDoCliente: number;
  readonly porPolitica: readonly { nome: string; medidos: number; excedidas: number }[];
  readonly estouradosAgora: number;
}

export function usePoliticasDeSla() {
  return useQuery({
    queryKey: ["sla", "politicas"],
    queryFn: () => chamar<{ itens: PoliticaDeSla[] }>("/api/painel/sla/politicas"),
  });
}

export function useRelatorioDeSla(dias: number) {
  return useQuery({
    queryKey: ["relatorio", "sla", dias],
    queryFn: () => chamar<RelatorioDeSla>(`/api/painel/relatorios/sla?dias=${dias}`),
  });
}

export async function salvarPoliticaDeSla(
  cliente: QueryClient,
  corpo: Record<string, unknown>,
): Promise<{ ok: boolean; falha?: FalhaDaApi }> {
  try {
    await chamar("/api/painel/sla/politicas", { metodo: "POST", corpo });
    await cliente.invalidateQueries({ queryKey: ["sla"] });
    await cliente.invalidateQueries({ queryKey: ["relatorio"] });
    return { ok: true };
  } catch (erro) {
    const falha = (erro as { falha?: FalhaDaApi }).falha;
    return falha ? { ok: false, falha } : { ok: false };
  }
}

export async function arquivarPoliticaDeSla(cliente: QueryClient, id: string): Promise<void> {
  await chamar(`/api/painel/sla/politicas/${id}`, { metodo: "DELETE" }).catch(() => null);
  await cliente.invalidateQueries({ queryKey: ["sla"] });
}

/**
 * O funil do FLUXO (§9 da spec 11) — onde a pessoa desiste.
 *
 * `quedaPercentual` é `null` em dois casos: ninguém passou pelo passo, e o passo é o `fim`. A tela mostra a
 * frase certa para cada um — um `0%` leria-se "este passo não perde ninguém", que é o oposto de "não há dado",
 * e um `100%` no `fim` apontaria o único passo que está necessariamente certo.
 */
export interface FunilDoFluxo {
  readonly passos: readonly {
    chave: string;
    tipo: string;
    execucoes: number;
    seguiram: number;
    quedaPercentual: number | null;
  }[];
  readonly totais: {
    execucoes: number;
    terminaram: number;
    esperando: number;
    estouraram: number;
  };
  readonly estouros: readonly { porque: string; quantas: number }[];
  readonly naoConcluiram: readonly {
    atendimentoId: string;
    protocolo: string;
    contato: string | null;
    passoAtual: string | null;
    desde: string;
  }[];
}

export function useFunilDoFluxo(dias: number) {
  return useQuery({
    queryKey: ["relatorio", "funil-do-fluxo", dias],
    queryFn: () => chamar<FunilDoFluxo>(`/api/painel/relatorios/funil-do-fluxo?dias=${dias}`),
  });
}

/**
 * Troca o perfil de acesso de alguém.
 *
 * O efeito acontece no Cert4All, em nome de quem está logado — ver
 * `src/painel/acessosDoCert4All.ts` no servidor. A sessão da pessoa afetada é
 * encerrada lá, e é por isso que a resposta traz um aviso em vez de um "pronto".
 */
export async function trocarPerfilDeAcesso(
  cliente: QueryClient,
  atendenteId: string,
  perfil: string,
): Promise<{ ok: boolean; aviso?: string; falha?: FalhaDaApi }> {
  try {
    const dados = await chamar<{ aviso?: string }>(
      `/api/painel/equipe/${encodeURIComponent(atendenteId)}/perfil`,
      { metodo: "PUT", corpo: { perfil } },
    );
    await cliente.invalidateQueries({ queryKey: ["equipe"] });
    return { ok: true, ...(dados?.aviso ? { aviso: dados.aviso } : {}) };
  } catch (erro) {
    const falha = (erro as { falha?: FalhaDaApi }).falha;
    return falha ? { ok: false, falha } : { ok: false };
  }
}

/** Convida alguém para a empresa — o fluxo é do Cert4All (72h, senha lá). */
export async function convidarParaEquipe(
  cliente: QueryClient,
  email: string,
  /** O perfil que a pessoa recebe ao aceitar. Sem ele, o Cert4All aplica o padrão: Atendente. */
  perfil?: string,
): Promise<{ ok: boolean; aviso?: string; falha?: FalhaDaApi }> {
  try {
    const dados = await chamar<{ aviso?: string }>("/api/painel/equipe/convites", {
      metodo: "POST",
      corpo: perfil ? { email, perfil } : { email },
    });
    await cliente.invalidateQueries({ queryKey: ["equipe"] });
    return { ok: true, ...(dados?.aviso ? { aviso: dados.aviso } : {}) };
  } catch (erro) {
    const falha = (erro as { falha?: FalhaDaApi }).falha;
    return falha ? { ok: false, falha } : { ok: false };
  }
}

/** Reenviar ou cancelar um convite pendente. */
export async function operarConviteDaEquipe(
  cliente: QueryClient,
  conviteId: string,
  acao: "cancelar" | "reenviar",
): Promise<{ ok: boolean; aviso?: string; falha?: FalhaDaApi }> {
  try {
    const dados = await chamar<{ aviso?: string }>(
      `/api/painel/equipe/convites/${encodeURIComponent(conviteId)}/${acao}`,
      { metodo: "POST", corpo: {} },
    );
    await cliente.invalidateQueries({ queryKey: ["equipe"] });
    return { ok: true, ...(dados?.aviso ? { aviso: dados.aviso } : {}) };
  } catch (erro) {
    const falha = (erro as { falha?: FalhaDaApi }).falha;
    return falha ? { ok: false, falha } : { ok: false };
  }
}


// --- Campanhas (spec 17) ---------------------------------------------------------------------------

export type ResultadoDoDestinatario =
  | "pendente" | "adiada" | "enviada" | "entregue" | "lida" | "respondeu"
  | "sem_whatsapp" | "parou_de_receber" | "limite_do_destinatario" | "falhou" | "pulada";

export interface Campanha {
  readonly id: string;
  readonly nome: string;
  readonly conexaoId: string;
  /** @cru */
  readonly numeroExibicao: string | null;
  readonly templateNome: string;
  readonly templateIdioma: string;
  readonly categoria: string | null;
  readonly estado: "rascunho" | "agendada" | "enviando" | "pausada" | "concluida" | "cancelada";
  readonly agendadaPara: string;
  readonly ritmoPorMinuto: number;
  readonly destinatarios: number;
  readonly tarifaCentavosPorMensagem: number;
  readonly custoEstimadoCentavos: number;
  readonly custoRealCentavos: number;
  readonly criadaEm: string;
  readonly criadaPor: string;
  readonly concluidaEm: string | null;
  readonly canceladaEm: string | null;
  readonly pausadaEm: string | null;
  readonly pausaMotivo: string | null;
  readonly pausadaPor: string | null;
  readonly contagens: Readonly<Record<ResultadoDoDestinatario, number>>;
  readonly frases: Readonly<Record<ResultadoDoDestinatario, string>>;
}

export interface PreviaDaCampanha {
  readonly previa: { casam: number; semOptin: number; bloqueados: number; semWhatsapp: number; alcancaveis: number; semNome: number; teto: number };
  readonly destinatarios: number;
  readonly categoria: string | null;
  readonly tarifaCentavosPorMensagem: number;
  readonly custoEstimadoCentavos: number;
  readonly conversasDaFranquia: number;
  readonly limiteDiario: { limite: number; lidoEm: string | null; suposto: boolean };
  readonly dias: number;
  readonly avisos: readonly string[];
}

export interface DestinatarioDaCampanha {
  readonly contatoId: string;
  readonly nome: string | null;
  /** @cru */
  readonly identificador: string;
  readonly resultado: ResultadoDoDestinatario;
  readonly erroCodigo: number | null;
  readonly erro: string | null;
  readonly atendimentoId: string | null;
  readonly atualizadoEm: string;
}

export function useCampanhas() {
  return useQuery({
    queryKey: ["campanhas"],
    queryFn: () => chamar<{ itens: Campanha[] }>("/api/painel/campanhas"),
    // A campanha em voo muda a cada rodada de 30s; a lista acompanha sem a pessoa recarregar.
    refetchInterval: 30_000,
  });
}

export function useDestinatarios(campanhaId: string | null, resultado: string | null) {
  return useQuery({
    queryKey: ["campanhas", campanhaId, "destinatarios", resultado],
    enabled: campanhaId !== null,
    queryFn: () => {
      const q = new URLSearchParams();
      if (resultado) q.set("resultado", resultado);
      return chamar<{ itens: DestinatarioDaCampanha[] }>(`/api/painel/campanhas/${campanhaId}/destinatarios?${q}`);
    },
  });
}

type Resposta<T> = { ok: true; dados: T } | { ok: false; falha?: FalhaDaApi };

async function tentar<T>(fn: () => Promise<T>): Promise<Resposta<T>> {
  try {
    return { ok: true, dados: await fn() };
  } catch (erro) {
    const falha = (erro as { falha?: FalhaDaApi }).falha;
    return falha ? { ok: false, falha } : { ok: false };
  }
}

export function previaDeCampanha(corpo: Record<string, unknown>): Promise<Resposta<PreviaDaCampanha>> {
  return tentar(() => chamar<PreviaDaCampanha>("/api/painel/campanhas/previa", { metodo: "POST", corpo }));
}

export async function criarCampanha(cliente: QueryClient, corpo: Record<string, unknown>): Promise<Resposta<PreviaDaCampanha & { id: string }>> {
  const r = await tentar(() => chamar<PreviaDaCampanha & { id: string }>("/api/painel/campanhas", { metodo: "POST", corpo }));
  if (r.ok) await cliente.invalidateQueries({ queryKey: ["campanhas"] });
  return r;
}

async function acaoDaCampanha(cliente: QueryClient, id: string, acao: "cancelar" | "pausar" | "retomar"): Promise<Resposta<unknown>> {
  const r = await tentar(() => chamar<unknown>(`/api/painel/campanhas/${id}/${acao}`, { metodo: "POST" }));
  await cliente.invalidateQueries({ queryKey: ["campanhas"] });
  return r;
}
export const cancelarCampanha = (c: QueryClient, id: string) => acaoDaCampanha(c, id, "cancelar");
export const pausarCampanha = (c: QueryClient, id: string) => acaoDaCampanha(c, id, "pausar");
export const retomarCampanha = (c: QueryClient, id: string) => acaoDaCampanha(c, id, "retomar");

export function sincronizarLimiteDoNumero(conexaoId: string): Promise<Resposta<{ tier: string | null; limite: number | null; qualidade: string | null }>> {
  return tentar(() => chamar(`/api/painel/conexoes/${conexaoId}/limite/sincronizar`, { metodo: "POST" }));
}

// ---------------------------------------------------------------------------------------------
// Estatísticas da campanha (spec 17 §9a) e o funil de oportunidades (spec 10)
// ---------------------------------------------------------------------------------------------

export interface DegrauDoFunilDaCampanha {
  readonly nome: string;
  readonly quantidade: number;
  readonly taxaDoAnterior: number | null;
  readonly taxaDoTotal: number;
}

export interface EstatisticasDaCampanha {
  readonly campanhaId: string;
  readonly funil: readonly DegrauDoFunilDaCampanha[];
  readonly lidas: number;
  readonly custoRealCentavos: number;
  readonly custoPorRespostaCentavos: number | null;
  readonly custoPorLeadGanhoCentavos: number | null;
  readonly seguradasPelaMeta: number;
  readonly optOuts: number;
  readonly taxaDeOptOut: number;
  readonly tempos: { readonly ateEntrega: number | null; readonly ateLeitura: number | null; readonly ateResposta: number | null };
  readonly conversas: {
    readonly abertas: number;
    readonly finalizadas: number;
    readonly fechadasSemResposta: number;
    readonly porMotivo: readonly { readonly motivo: string; readonly quantidade: number }[];
  };
  readonly leads: { readonly abertos: number; readonly ganhos: number; readonly perdidos: number; readonly valorGanhoCentavos: number };
  readonly saude: {
    readonly qualidadeNoInicio: string | null;
    readonly qualidadeNoFim: string | null;
    readonly limiteNoInicio: number | null;
    readonly limiteNoFim: number | null;
  };
}

export function useEstatisticasDaCampanha(campanhaId: string | null) {
  return useQuery({
    queryKey: ["campanhas", campanhaId, "estatisticas"],
    enabled: campanhaId !== null,
    queryFn: () => chamar<EstatisticasDaCampanha>(`/api/painel/campanhas/${campanhaId}/estatisticas`),
    refetchInterval: 30_000,
  });
}

export type OrigemDaOportunidade = "campanha" | "conversa" | "anuncio" | "manual";

export interface EtapaDoFunil {
  readonly id: string;
  readonly nome: string;
  readonly ordem: number;
  readonly probabilidadePct: number;
  readonly tipo: "aberta" | "ganho" | "perdido";
}

export interface Oportunidade {
  readonly id: string;
  readonly quadroId: string;
  readonly etapaId: string;
  /** Os campos personalizados do quadro, chaveados pelo id do campo. */
  readonly campos: Readonly<Record<string, string | number | null>>;
  readonly contatoId: string;
  readonly contatoNome: string | null;
  /** @cru */
  readonly identificador: string;
  readonly titulo: string | null;
  /** Nulo sem `read_funnel_values` — o servidor não manda. */
  readonly valorCentavos: number | null;
  readonly temperatura: "quente" | "morno" | "frio" | null;
  readonly origem: OrigemDaOportunidade;
  readonly responsavelId: string | null;
  readonly responsavelNome: string | null;
  readonly status: "aberta" | "ganha" | "perdida";
  readonly motivoDePerda: string | null;
  readonly previsaoDeFechamento: string | null;
  readonly observacao: string | null;
  readonly atendimentoId: string | null;
  readonly protocolo: string | null;
  readonly campanhaId: string | null;
  readonly campanhaNome: string | null;
  readonly conversa: ConversaDoCard | null;
  /** A próxima tarefa pendente do card (a agenda); `null` sem tarefa. */
  readonly tarefa: TarefaDoCard | null;
  /** Quantos itens do catálogo compõem o card. Com itens, o valor é a soma e não se digita. */
  readonly itens: number;
  readonly etapaDesde: string;
  readonly diasNaEtapa: number;
  readonly criadaEm: string;
  readonly fechadaEm: string | null;
}

export interface TarefaDoCard {
  readonly id: string;
  readonly tipo: "lembrete" | "mensagem";
  readonly quando: string;
  readonly texto: string;
  readonly estado: "pendente" | "feito" | "falhou" | "cancelado";
  readonly vencida: boolean;
  readonly paraAtendenteId: string | null;
  readonly paraAtendenteNome: string | null;
  readonly concluidoEm: string | null;
}

export interface QuadroResumo {
  readonly id: string;
  readonly nome: string;
  readonly padrao: boolean;
}
export interface CampoDoFunil {
  readonly id: string;
  readonly nome: string;
  readonly tipo: "texto" | "numero" | "data" | "opcao";
  readonly opcoes: readonly string[];
  readonly ordem: number;
}

export interface QuadroDoFunil {
  readonly quadroId: string;
  readonly quadros: readonly QuadroResumo[];
  readonly campos: readonly CampoDoFunil[];
  readonly etapas: readonly EtapaDoFunil[];
  readonly cards: readonly Oportunidade[];
  readonly motivosDePerda: readonly { readonly id: string; readonly nome: string }[];
  readonly previsaoCentavos: number | null;
  readonly verDoTime: boolean;
  readonly verValores: boolean;
}

export function useFunil(dias = 30, campanhaId: string | null = null, quadroId: string | null = null) {
  return useQuery({
    queryKey: ["funil", dias, campanhaId, quadroId],
    queryFn: () => chamar<QuadroDoFunil>(`/api/painel/funil?dias=${dias}${campanhaId ? `&campanha=${encodeURIComponent(campanhaId)}` : ""}${quadroId ? `&quadro=${encodeURIComponent(quadroId)}` : ""}`),
    // A campanha abre card sozinha a cada conciliação (30s); o quadro acompanha.
    refetchInterval: 30_000,
  });
}

async function acaoDoFunil<T>(cliente: QueryClient, fn: () => Promise<T>): Promise<Resposta<T>> {
  const r = await tentar(fn);
  await cliente.invalidateQueries({ queryKey: ["funil"] });
  return r;
}
export const criarOportunidade = (c: QueryClient, corpo: Record<string, unknown>) =>
  acaoDoFunil(c, () => chamar<{ id: string }>("/api/painel/funil/oportunidades", { metodo: "POST", corpo }));
export const editarOportunidade = (c: QueryClient, id: string, corpo: Record<string, unknown>) =>
  acaoDoFunil(c, () => chamar<unknown>(`/api/painel/funil/oportunidades/${id}`, { metodo: "PATCH", corpo }));
export const moverOportunidade = (c: QueryClient, id: string, etapaId: string) =>
  acaoDoFunil(c, () => chamar<unknown>(`/api/painel/funil/oportunidades/${id}/mover`, { metodo: "POST", corpo: { etapaId } }));
export const ganharOportunidade = (c: QueryClient, id: string, valorCentavos?: number) =>
  acaoDoFunil(c, () => chamar<unknown>(`/api/painel/funil/oportunidades/${id}/ganhar`, { metodo: "POST", corpo: valorCentavos === undefined ? {} : { valorCentavos } }));
export const perderOportunidade = (c: QueryClient, id: string, motivoDePerdaId: string) =>
  acaoDoFunil(c, () => chamar<unknown>(`/api/painel/funil/oportunidades/${id}/perder`, { metodo: "POST", corpo: { motivoDePerdaId } }));
export const reabrirOportunidade = (c: QueryClient, id: string) =>
  acaoDoFunil(c, () => chamar<unknown>(`/api/painel/funil/oportunidades/${id}/reabrir`, { metodo: "POST" }));

// --- O kanban rastreia a campanha, diz com quem o contato está, e mostra a conversa da campanha ---

export interface ConversaDoCard {
  readonly atendimentoId: string;
  readonly protocolo: string | null;
  readonly estado: "aguardando" | "com_agente" | "em_atendimento" | "finalizado";
  readonly comQuem: "fila" | "humano" | "ia" | "integracao" | "finalizada";
  readonly donoNome: string | null;
  readonly filaId: string | null;
  readonly conexaoId: string | null;
}

export interface MensagemDaConversaDoCard {
  readonly id: string;
  readonly direcao: "recebida" | "enviada" | "eco";
  readonly tipo: string;
  readonly texto: string | null;
  readonly expiraEm: string | null;
  readonly ocorridoEm: string | null;
  readonly statusDeEnvio: "enfileirado" | "sent" | "delivered" | "read" | "failed" | null;
  readonly erroDeEnvio: string | null;
  readonly enviadaPorNome: string | null;
  readonly midia: { readonly mime: string | null; readonly nome: string | null; readonly transcricao: string | null } | null;
}

export interface AtendimentoDoCard {
  readonly id: string;
  readonly protocolo: string;
  readonly estado: ConversaDoCard["estado"];
  readonly abertoEm: string;
  readonly finalizadoEm: string | null;
  readonly motivo: string | null;
  readonly visivel: boolean;
  readonly mensagens: readonly MensagemDaConversaDoCard[];
}

export interface ConversaDaOportunidade {
  readonly campanhaId: string | null;
  readonly campanhaNome: string | null;
  readonly atendimentos: readonly AtendimentoDoCard[];
}

const chaveDaConversa = (id: string) => ["funil", "conversa", id] as const;
const buscarConversaDaOportunidade = (id: string) => chamar<ConversaDaOportunidade>(`/api/painel/funil/oportunidades/${id}/conversa`);

export function useConversaDaOportunidade(oportunidadeId: string | null) {
  return useQuery({
    queryKey: chaveDaConversa(oportunidadeId ?? ""),
    enabled: oportunidadeId !== null,
    queryFn: () => buscarConversaDaOportunidade(oportunidadeId ?? ""),
    // O clique reusa o que o hover já trouxe (`prefetchConversa`); 10s de frescor bastam para isso.
    staleTime: 10_000,
    refetchInterval: 15_000,
  });
}

/**
 * Pré-carrega a conversa do card ao passar o mouse: o que demorava no clique era a ida à rede que só
 * começava nele. Com o hover, ela começa antes — o clique encontra o dado no cache.
 */
export function prefetchConversa(cliente: QueryClient, id: string): void {
  void cliente.prefetchQuery({ queryKey: chaveDaConversa(id), queryFn: () => buscarConversaDaOportunidade(id), staleTime: 10_000 });
}

// --- O catálogo e os itens do card (spec 10 §6) ----------------------------------------------------

export interface ItemDoCatalogo {
  readonly id: string;
  readonly nome: string;
  readonly sku: string | null;
  readonly precoCentavos: number;
  readonly arquivadoEm: string | null;
}
export interface ItemDoCard {
  readonly id: string;
  readonly itemId: string;
  readonly nome: string;
  readonly sku: string | null;
  readonly quantidade: number;
  readonly precoCentavos: number | null;
  readonly totalCentavos: number | null;
}
export function useCatalogoDoFunil() {
  return useQuery({ queryKey: ["funil", "catalogo"], queryFn: () => chamar<{ itens: ItemDoCatalogo[] }>("/api/painel/funil/catalogo") });
}
export const criarItemDoCatalogo = (c: QueryClient, corpo: { nome: string; sku?: string | null; precoCentavos: number }) =>
  acaoDoFunil(c, () => chamar<{ id: string }>("/api/painel/funil/catalogo", { metodo: "POST", corpo }));
export const editarItemDoCatalogo = (c: QueryClient, id: string, corpo: { nome?: string; sku?: string | null; precoCentavos?: number }) =>
  acaoDoFunil(c, () => chamar<unknown>(`/api/painel/funil/catalogo/${id}`, { metodo: "PATCH", corpo }));
export const arquivarItemDoCatalogo = (c: QueryClient, id: string) =>
  acaoDoFunil(c, () => chamar<unknown>(`/api/painel/funil/catalogo/${id}`, { metodo: "DELETE" }));
export function useItensDaOportunidade(oportunidadeId: string | null) {
  return useQuery({
    queryKey: ["funil", "itens", oportunidadeId ?? ""],
    enabled: oportunidadeId !== null,
    queryFn: () => chamar<{ itens: ItemDoCard[]; totalCentavos: number | null }>(`/api/painel/funil/oportunidades/${oportunidadeId ?? ""}/itens`),
  });
}
export const adicionarItemNoCard = (c: QueryClient, id: string, corpo: { itemId: string; quantidade: number }) =>
  acaoDoFunil(c, () => chamar<{ id: string; valorCentavos: number }>(`/api/painel/funil/oportunidades/${id}/itens`, { metodo: "POST", corpo }));
export const removerItemDoCard = (c: QueryClient, id: string, itemDoCardId: string) =>
  acaoDoFunil(c, () => chamar<{ valorCentavos: number | null }>(`/api/painel/funil/oportunidades/${id}/itens/${itemDoCardId}`, { metodo: "DELETE" }));

// --- As tarefas do card (spec 10 §6) — são a agenda, com o card como dono --------------------------

const chaveDasTarefas = (id: string) => ["funil", "tarefas", id] as const;
export function useTarefasDaOportunidade(oportunidadeId: string | null) {
  return useQuery({
    queryKey: chaveDasTarefas(oportunidadeId ?? ""),
    enabled: oportunidadeId !== null,
    queryFn: () => chamar<{ tarefas: TarefaDoCard[] }>(`/api/painel/funil/oportunidades/${oportunidadeId ?? ""}/tarefas`),
  });
}
export const criarTarefa = (c: QueryClient, id: string, corpo: { texto: string; quando: string; paraAtendenteId?: string | null }) =>
  acaoDoFunil(c, () => chamar<{ id: string }>(`/api/painel/funil/oportunidades/${id}/tarefas`, { metodo: "POST", corpo }));
export const concluirTarefa = (c: QueryClient, id: string, tarefaId: string) =>
  acaoDoFunil(c, () => chamar<unknown>(`/api/painel/funil/oportunidades/${id}/tarefas/${tarefaId}/concluir`, { metodo: "POST" }));
export const cancelarTarefa = (c: QueryClient, id: string, tarefaId: string) =>
  acaoDoFunil(c, () => chamar<unknown>(`/api/painel/funil/oportunidades/${id}/tarefas/${tarefaId}`, { metodo: "DELETE" }));

export const passarParaAFila = (c: QueryClient, id: string) =>
  acaoDoFunil(c, () => chamar<{ atendimentoId: string; filaId: string }>(`/api/painel/funil/oportunidades/${id}/passar-para-a-fila`, { metodo: "POST" }));

export const responderPelaOportunidade = (
  c: QueryClient,
  id: string,
  corpo: { texto?: string; template?: { nome: string; idioma: string; variaveis: string[] } },
) => acaoDoFunil(c, () => chamar<{ atendimentoId: string }>(`/api/painel/funil/oportunidades/${id}/responder`, { metodo: "POST", corpo }));


// --- Etapas e motivos de perda pela tela (spec 10) -------------------------------------------------

// --- Múltiplos quadros e campos personalizados (spec 10 §6) ---------------------------------------
export function useEtapasDeTodosOsQuadros() {
  return useQuery({ queryKey: ["funil", "etapas-de-todos"], queryFn: () => chamar<{ etapas: (EtapaDoFunil & { quadroId: string; quadroNome: string })[] }>("/api/painel/funil/etapas") });
}
export const criarQuadro = (c: QueryClient, nome: string) =>
  acaoDoFunil(c, () => chamar<{ id: string }>("/api/painel/funil/quadros", { metodo: "POST", corpo: { nome } }));
export const editarQuadro = (c: QueryClient, id: string, corpo: { nome?: string; padrao?: boolean }) =>
  acaoDoFunil(c, () => chamar<unknown>(`/api/painel/funil/quadros/${id}`, { metodo: "PATCH", corpo }));
export const arquivarQuadro = (c: QueryClient, id: string) =>
  acaoDoFunil(c, () => chamar<unknown>(`/api/painel/funil/quadros/${id}`, { metodo: "DELETE" }));
export const criarCampo = (c: QueryClient, quadroId: string, corpo: { nome: string; tipo: CampoDoFunil["tipo"]; opcoes?: string[] }) =>
  acaoDoFunil(c, () => chamar<{ id: string }>(`/api/painel/funil/quadros/${quadroId}/campos`, { metodo: "POST", corpo }));
export const arquivarCampo = (c: QueryClient, id: string) =>
  acaoDoFunil(c, () => chamar<unknown>(`/api/painel/funil/campos/${id}`, { metodo: "DELETE" }));

export const criarEtapa = (c: QueryClient, corpo: { nome: string; probabilidadePct: number; quadroId?: string }) =>
  acaoDoFunil(c, () => chamar<{ id: string }>("/api/painel/funil/etapas", { metodo: "POST", corpo }));
export const editarEtapa = (c: QueryClient, id: string, corpo: { nome?: string; probabilidadePct?: number; ordem?: number }) =>
  acaoDoFunil(c, () => chamar<unknown>(`/api/painel/funil/etapas/${id}`, { metodo: "PATCH", corpo }));
export const arquivarEtapa = (c: QueryClient, id: string) =>
  acaoDoFunil(c, () => chamar<unknown>(`/api/painel/funil/etapas/${id}`, { metodo: "DELETE" }));
export const criarMotivoDePerda = (c: QueryClient, nome: string) =>
  acaoDoFunil(c, () => chamar<{ id: string }>("/api/painel/funil/motivos-de-perda", { metodo: "POST", corpo: { nome } }));
export const arquivarMotivoDePerda = (c: QueryClient, id: string) =>
  acaoDoFunil(c, () => chamar<unknown>(`/api/painel/funil/motivos-de-perda/${id}`, { metodo: "DELETE" }));


// --- O relatório do funil (spec 10 §3.2) --------------------------------------------------------

export interface RelatorioDoFunil {
  readonly dias: number;
  readonly abertas: number;
  readonly ganhas: number;
  readonly perdidas: number;
  readonly taxaDeGanho: number | null;
  readonly valorGanhoCentavos: number | null;
  readonly valorPerdidoCentavos: number | null;
  readonly perdasPorMotivo: readonly { readonly motivo: string; readonly quantidade: number; readonly valorCentavos: number | null }[];
  readonly porOrigem: readonly { readonly origem: OrigemDaOportunidade; readonly abertas: number; readonly ganhas: number; readonly perdidas: number }[];
  readonly diasAteFechar: { readonly mediana: number; readonly p90: number; readonly amostra: number } | null;
  readonly quadroId: string | null;
  readonly quadros: readonly QuadroResumo[];
  readonly previsao: {
    readonly totalCentavos: number;
    readonly porMes: readonly { readonly mes: string | null; readonly centavos: number; readonly cards: number }[];
    readonly porResponsavel: readonly { readonly responsavelId: string | null; readonly nome: string | null; readonly centavos: number; readonly cards: number }[];
  } | null;
  readonly verDoTime: boolean;
  readonly verValores: boolean;
}

export function useRelatorioDoFunil(dias: number, quadroId: string | null = null) {
  return useQuery({
    queryKey: ["relatorio", "funil", dias, quadroId],
    queryFn: () => chamar<RelatorioDoFunil>(`/api/painel/relatorios/funil?dias=${dias}${quadroId ? `&quadro=${encodeURIComponent(quadroId)}` : ""}`),
  });
}


// --- Importar oportunidades (spec 10 §6) --------------------------------------------------------

export interface ConferenciaDeOportunidades {
  readonly amostra: readonly { linha: number; /** @cru */ identificador: string; nome: string | null; titulo: string | null; valorCentavos: number | null; etapa: string | null; temperatura: string | null; previsaoDeFechamento: string | null }[];
  readonly quantasAceitas: number;
  readonly recusadas: readonly { linha: number; conteudo: string; problema: string; explicacao: string }[];
  readonly quantasRecusadas: number;
  readonly etapasDesconhecidas: readonly string[];
  readonly colunasExtras: readonly { readonly titulo: string; readonly campoId: string | null; readonly campoNome: string | null; readonly tipo: CampoDoFunil["tipo"] | null }[];
  readonly camposIlegiveis: number;
}

export function conferirOportunidades(csv: string, quadroId: string | null = null): Promise<ConferenciaDeOportunidades> {
  return chamar<ConferenciaDeOportunidades>("/api/painel/funil/importar/conferir", { metodo: "POST", corpo: { csv, quadroId } });
}

export async function importarOportunidades(cliente: QueryClient, csv: string, quadroId: string | null = null): Promise<Resposta<{ criadas: number; jaTinham: number; contatosCriados: number; recusadas: readonly unknown[]; etapasDesconhecidas: readonly string[]; camposIgnorados: number }>> {
  const r = await tentar(() => chamar<{ criadas: number; jaTinham: number; contatosCriados: number; recusadas: readonly unknown[]; etapasDesconhecidas: readonly string[]; camposIgnorados: number }>("/api/painel/funil/importar", { metodo: "POST", corpo: { csv, quadroId } }));
  await cliente.invalidateQueries({ queryKey: ["funil"] });
  await cliente.invalidateQueries({ queryKey: ["contatos"] });
  return r;
}

// --- Publicações em Grupos (spec 18) ------------------------------------------------------------------

export interface ConexaoParaPublicar {
  readonly id: string;
  readonly apelido: string | null;
  /** @cru */
  readonly numeroExibicao: string | null;
  /** @cru */
  readonly endereco: string | null;
  readonly estado: string;
  readonly disponivel: boolean;
  readonly motivo: string | null;
  readonly ultimaSincronizacaoEm: string | null;
  readonly gruposAtivos: number;
}

export interface EtiquetaDeGrupo {
  readonly id: string;
  readonly nome: string;
  readonly cor: string | null;
  readonly grupos: number;
}

export interface Grupo {
  readonly id: string;
  readonly idExterno: string;
  readonly nome: string;
  readonly descricao: string | null;
  readonly participantes: number;
  readonly admins: number;
  readonly permissaoDeMensagem: "everyone" | "admins_only" | "unknown";
  readonly interacaoDeParticipantes: string;
  readonly metadadosDisponiveis: boolean;
  readonly ativo: boolean;
  readonly gerenciado: boolean;
  readonly ultimaSincronizacaoEm: string;
  readonly etiquetas: readonly { readonly id: string; readonly nome: string; readonly cor: string | null }[];
  readonly conexoes: readonly { readonly id: string; readonly apelido: string | null; /** @cru */ readonly numeroExibicao: string | null; readonly somosAdmin: boolean; readonly ativo: boolean }[];
  readonly capacidades: Record<string, unknown>;
}

export interface Recorrencia {
  readonly dias: readonly number[];
  readonly horarios: readonly string[];
  readonly inicio: string;
  readonly fim: string;
  readonly fuso: string;
}

export type EstadoDePublicacao = "agendada" | "executando" | "pausada" | "aguardando_conexao" | "freada" | "concluida" | "parcialmente_concluida" | "falhou" | "cancelada";

export interface Publicacao {
  readonly id: string;
  readonly nome: string;
  readonly tipoDeConteudo: "texto" | "imagem" | "video" | "documento" | "enquete" | "botoes";
  readonly texto: string;
  /** As versões extras do texto (0095), revezadas entre os grupos. O `texto` é a versão 1. */
  readonly variacoes?: readonly string[];
  readonly midiaId: string | null;
  readonly conexaoId: string;
  readonly conexaoApelido: string | null;
  readonly conexaoNumero: string | null;
  /** Os números que revezam com o principal (0097). Vazio ou ausente = a publicação sai por um número só. */
  readonly remetentesExtras?: readonly { readonly id: string; readonly apelido: string | null; /** @cru */ readonly numeroExibicao: string | null }[];
  readonly estado: EstadoDePublicacao;
  readonly agendadaPara: string;
  readonly recorrencia: Recorrencia | null;
  readonly proximaOcorrenciaEm: string | null;
  readonly rastrearLinks: boolean;
  readonly mencionarTodos?: boolean;
  readonly ritmoPorMinuto: number;
  readonly alvos: number;
  readonly criadaPor: string;
  readonly criadaEm: string;
  readonly pausaMotivo: string | null;
  readonly canceladaEm: string | null;
  readonly concluidaEm: string | null;
  readonly execucoes: number;
  readonly enviados: number;
  readonly falhados: number;
  readonly audiencia: number;
  readonly reacoes: number;
  readonly respostas: number;
  readonly cliques: number | null;
  readonly engajamento: number | null;
}

export interface ExecucaoDePublicacao {
  readonly id: string;
  readonly previstaPara: string;
  readonly estado: string;
  readonly iniciadaEm: string | null;
  readonly concluidaEm: string | null;
  readonly enviados: number;
  readonly falhados: number;
  readonly cancelados: number;
  readonly pendentes: number;
  readonly audienciaPotencial: number;
  readonly motivo: string | null;
}

export interface EntregaDePublicacao {
  /** Qual versão do texto este grupo recebeu (0095): 0 é o texto principal. */
  readonly variacao?: number | null;
  readonly id: string;
  readonly execucaoId: string;
  readonly grupoId: string;
  readonly grupoNome: string;
  readonly conexaoNumero: string | null;
  readonly conexaoApelido: string | null;
  readonly idExternoDaMensagem: string | null;
  readonly previstaPara: string;
  readonly enviadaEm: string | null;
  readonly estado: string;
  readonly tentativas: number;
  readonly erroCodigo: string | null;
  readonly erro: string | null;
  readonly participantesNoEnvio: number | null;
}

export interface DetalheDaPublicacao {
  readonly publicacao: Publicacao;
  readonly execucoes: readonly ExecucaoDePublicacao[];
  readonly alvos: readonly { readonly grupoId: string; readonly nome: string; readonly participantes: number }[];
  readonly eventos: readonly { readonly tipo: string; readonly atorId: string | null; readonly atorNome: string | null; readonly de: unknown; readonly para: unknown; readonly motivo: string | null; readonly ocorridoEm: string }[];
  /** Cada número da publicação, agora (0097). */
  readonly remetentes?: readonly RemetenteDaPublicacao[];
}

export interface Metrica {
  readonly valor: number | null;
  readonly disponivel: boolean;
  readonly motivo: string | null;
}

/** O resultado de uma versão do texto (0095). Números, não veredito — `amostraPequena` diz quando é pouco. */
/** O resultado de um número do rodízio (0097) — números, não veredito. */
export interface ResultadoDoRemetente {
  readonly conexaoId: string;
  readonly apelido: string | null;
  /** @cru */
  readonly numeroExibicao: string | null;
  readonly grupos: number;
  readonly reacoes: number;
  readonly respostas: Metrica;
  readonly interacoesPorGrupo: number | null;
  readonly amostraPequena: boolean;
}

/** Um remetente da publicação AGORA (0097): no ar ou não, o que o freio diz, e quanto da parte dele saiu. */
export interface RemetenteDaPublicacao {
  readonly conexaoId: string;
  readonly apelido: string | null;
  /** @cru */
  readonly numeroExibicao: string | null;
  readonly principal: boolean;
  readonly disponivel: boolean;
  readonly motivo: string | null;
  readonly freio: { readonly acao: "seguir" | "desacelerar" | "pausar"; readonly motivo: string | null; readonly ate: string | null };
  readonly enviadas: number;
  readonly pendentes: number;
  readonly falhadas: number;
}

export interface ResultadoDaVersao {
  readonly versao: number;
  readonly texto: string;
  readonly grupos: number;
  readonly reacoes: number;
  readonly respostas: Metrica;
  readonly interacoesPorGrupo: number | null;
  readonly amostraPequena: boolean;
}

export interface AnaliseDaPublicacao {
  readonly gruposSelecionados: number;
  readonly enviados: number;
  readonly falhados: number;
  readonly audienciaPotencial: number;
  readonly reacoes: Metrica;
  readonly respostas: Metrica;
  readonly cliques: Metrica;
  readonly participantesDistintos: Metrica;
  readonly mensagensApos24h: Metrica;
  readonly gruposComAtividade: Metrica;
  readonly segundosAteAPrimeiraInteracao: Metrica;
  readonly taxaDeEngajamento: Metrica;
  readonly audienciaUnica: Metrica;
  readonly sobreposicao: Metrica;
  readonly meiaVidaSegundos: Metrica;
  readonly curva24h: readonly number[];
  readonly entradas24h: Metrica;
  readonly saidas24h: Metrica;
  readonly saldoDeParticipantes: Metrica;
  readonly concentracao: Metrica;
  readonly uplift: Metrica;
  readonly reacoesPorEmoji: readonly { readonly emoji: string; readonly quantas: number; readonly fatia: number }[];
  readonly encaminhamentos: Metrica;
  readonly gruposQueEncaminharam: number;
  readonly encaminhamentosForaDosAlvos: number;
  /** Uma linha por versão do texto (0095). Vazio/ausente quando a publicação tem um texto só. */
  readonly porVariacao?: readonly ResultadoDaVersao[];
  /** O resultado por número do rodízio (0097). Vazio com um número só. */
  readonly porRemetente?: readonly ResultadoDoRemetente[];
  readonly entregas: readonly {
    readonly variacao?: number | null;
    readonly entregaId: string;
    readonly grupoNome: string;
    readonly permissaoDeMensagem: string;
    readonly reacoes: Metrica;
    readonly respostas: Metrica;
    readonly mensagens24h: Metrica;
    readonly confirmaramEntrega: Metrica;
    readonly confirmaramLeitura: Metrica;
    readonly cliques: Metrica;
    readonly entradas24h: Metrica;
    readonly saidas24h: Metrica;
  }[];
  readonly nota: string;
}

export interface ItemDoCalendario {
  readonly publicacaoId: string;
  readonly execucaoId: string | null;
  readonly nome: string;
  readonly quando: string;
  readonly estado: string;
  readonly grupos: number;
  readonly conexaoApelido: string | null;
  readonly conexaoNumero: string | null;
  readonly tipoDeConteudo: string;
  readonly prevista: boolean;
}

export interface ResumoDePublicacoes {
  readonly proximas: readonly ItemDoCalendario[];
  readonly hoje: readonly ItemDoCalendario[];
  readonly emExecucao: readonly Publicacao[];
  readonly precisamDeAtencao: readonly Publicacao[];
  readonly ultimas: readonly Publicacao[];
  readonly grupos: { readonly ativos: number; readonly conversacionais: number; readonly deAviso: number; readonly semEtiqueta: number };
  readonly ultimos30Dias: { readonly publicacoes: number; readonly entregas: number; readonly grupos: number; readonly audiencia: number; readonly reacoes: number; readonly respostas: number; readonly cliques: number; readonly engajamento: number | null };
  readonly insights: {
    readonly horarioComMaisAtividade: { readonly hora: number; readonly interacoesPorEntrega: number; readonly entregas: number } | null;
    readonly formatoComMaisAtividade: { readonly formato: string; readonly interacoesPorEntrega: number; readonly entregas: number } | null;
    readonly gruposMaisResponsivos: readonly { readonly grupoId: string; readonly nome: string; readonly interacoesPorEntrega: number; readonly entregas: number }[];
    readonly nota: string;
  };
}

export interface PreviaDaPublicacao {
  readonly grupos: number;
  readonly audienciaPotencial: number;
  readonly foraDaConexao: number;
  readonly semPermissao: number;
  readonly primeiraOcorrencia: string | null;
  readonly ocorrencias: number;
  readonly proximasOcorrencias: readonly string[];
  readonly conexaoDisponivel: boolean;
  readonly conexaoMotivo: string | null;
  readonly rastreioDisponivel: boolean;
  readonly linksNoTexto: number;
  readonly gruposIncluidos: number | null;
  readonly ritmoPorMinuto: number;
  readonly duracaoDeCadaEnvioS: number;
  /** No rodízio (0097), quantos grupos cada número leva. Vazio com um número só. */
  readonly porRemetente?: readonly { readonly conexaoId: string; readonly grupos: number }[];
  readonly avisos: readonly string[];
}

export const chavesDePublicacoes = {
  grupos: (filtro: Record<string, string>) => ["grupos", filtro] as const,
  publicacoes: () => ["publicacoes"] as const,
  publicacao: (id: string) => ["publicacao", id] as const,
  entregas: (id: string, execucaoId: string | null) => ["publicacao-entregas", id, execucaoId] as const,
  analise: (id: string, execucaoId: string | null) => ["publicacao-analise", id, execucaoId] as const,
  calendario: (de: string, ate: string) => ["publicacoes-calendario", de, ate] as const,
  resumo: () => ["publicacoes-resumo"] as const,
} as const;

// --- Portas de entrada (H103-A) ---------------------------------------------------------------------

export interface PortaNaLista {
  readonly id: string;
  readonly nome: string;
  readonly codigo: string;
  readonly estado: "rascunho" | "ativa" | "encerrada";
  readonly venceEm: string | null;
  readonly grupos: number;
  /** O que ainda cabe. Zero numa porta ativa = ela esta' mandando gente para a pagina de espera AGORA. */
  readonly vagas: number;
  readonly entrou: number;
  readonly semVaga: number;
  readonly encerrada: number;
  readonly criadaEm: string;
}

export interface GrupoDaPorta {
  readonly grupoId: string;
  readonly nome: string;
  readonly ordem: number;
  readonly limite: number;
  readonly participantes: number;
  readonly vagas: number;
  readonly temConvite: boolean;
  readonly conviteEm: string | null;
  readonly link: string | null;
  readonly ativo: boolean;
  readonly gerenciado: boolean;
  readonly grupoAtivo: boolean;
}

export const chavesDePortas = {
  lista: () => ["portas"] as const,
  uma: (id: string) => ["porta", id] as const,
} as const;

export function usePortas() {
  return useQuery({
    queryKey: chavesDePortas.lista(),
    queryFn: () => chamar<{ itens: readonly PortaNaLista[]; conexoes: readonly ConexaoParaPublicar[] }>("/api/painel/portas"),
  });
}

export function usePorta(id: string | null) {
  return useQuery({
    queryKey: chavesDePortas.uma(id ?? ""),
    enabled: Boolean(id),
    queryFn: () => chamar<{ porta: Record<string, unknown>; grupos: readonly GrupoDaPorta[] }>(`/api/painel/portas/${id}`),
  });
}

// --- Gatilhos de grupo (H103-B) ---------------------------------------------------------------------

export interface GatilhoNaLista {
  readonly id: string;
  readonly grupoId: string;
  readonly grupoNome: string;
  readonly conexaoId: string;
  readonly conexaoRotulo: string | null;
  readonly evento: "entrada" | "saida" | "lotou";
  readonly ativo: boolean;
  readonly texto: string;
  readonly limite: number | null;
  readonly ritmoPorMinuto: number;
  readonly participantes: number;
  readonly enviados: number;
  readonly pulados: number;
  readonly falhados: number;
}

export const chavesDeGatilhos = { lista: () => ["gatilhos"] as const } as const;

export function useGatilhos() {
  return useQuery({
    queryKey: chavesDeGatilhos.lista(),
    queryFn: () => chamar<{ itens: readonly GatilhoNaLista[]; conexoes: readonly ConexaoParaPublicar[] }>("/api/painel/gatilhos"),
  });
}

export function useGrupos(filtro: { busca?: string; etiqueta?: string; conexao?: string }) {
  const q = new URLSearchParams();
  if (filtro.busca) q.set("busca", filtro.busca);
  if (filtro.etiqueta) q.set("etiqueta", filtro.etiqueta);
  if (filtro.conexao) q.set("conexao", filtro.conexao);
  const s = q.toString();
  return useQuery({
    queryKey: chavesDePublicacoes.grupos(Object.fromEntries(q)),
    queryFn: () => chamar<{ itens: readonly Grupo[]; conexoes: readonly ConexaoParaPublicar[]; etiquetas: readonly EtiquetaDeGrupo[] }>(`/api/painel/grupos${s ? `?${s}` : ""}`),
  });
}

export function usePublicacoes() {
  return useQuery({
    queryKey: chavesDePublicacoes.publicacoes(),
    queryFn: () => chamar<{ itens: readonly Publicacao[]; conexoes: readonly ConexaoParaPublicar[] }>("/api/painel/publicacoes"),
    refetchInterval: 30_000,
  });
}

export function usePublicacao(id: string | null) {
  return useQuery({
    queryKey: chavesDePublicacoes.publicacao(id ?? ""),
    queryFn: () => chamar<DetalheDaPublicacao>(`/api/painel/publicacoes/${id}`),
    enabled: id !== null,
    refetchInterval: 15_000,
  });
}

export function useEntregasDaPublicacao(id: string | null, execucaoId: string | null) {
  return useQuery({
    queryKey: chavesDePublicacoes.entregas(id ?? "", execucaoId),
    queryFn: () => chamar<{ itens: readonly EntregaDePublicacao[] }>(`/api/painel/publicacoes/${id}/entregas${execucaoId ? `?execucao=${execucaoId}` : ""}`),
    enabled: id !== null,
    refetchInterval: 15_000,
  });
}

export function useAnaliseDaPublicacao(id: string | null, execucaoId: string | null) {
  return useQuery({
    queryKey: chavesDePublicacoes.analise(id ?? "", execucaoId),
    queryFn: () => chamar<AnaliseDaPublicacao>(`/api/painel/publicacoes/${id}/analise${execucaoId ? `?execucao=${execucaoId}` : ""}`),
    enabled: id !== null,
  });
}

export function useCalendarioDePublicacoes(de: string, ate: string) {
  return useQuery({
    queryKey: chavesDePublicacoes.calendario(de, ate),
    queryFn: () => chamar<{ itens: readonly ItemDoCalendario[] }>(`/api/painel/publicacoes/calendario?de=${encodeURIComponent(de)}&ate=${encodeURIComponent(ate)}`),
  });
}

export function useResumoDePublicacoes() {
  return useQuery({
    queryKey: chavesDePublicacoes.resumo(),
    queryFn: () => chamar<ResumoDePublicacoes>("/api/painel/publicacoes/resumo"),
    refetchInterval: 30_000,
  });
}

// `tentar` e `Resposta` são os mesmos das campanhas, acima.

function invalidarPublicacoes(cliente: QueryClient): void {
  void cliente.invalidateQueries({ queryKey: ["publicacoes"] });
  void cliente.invalidateQueries({ queryKey: ["publicacao"] });
  void cliente.invalidateQueries({ queryKey: ["publicacao-entregas"] });
  void cliente.invalidateQueries({ queryKey: ["publicacoes-calendario"] });
  void cliente.invalidateQueries({ queryKey: ["publicacoes-resumo"] });
}

/**
 * ADMINISTRAR O GRUPO — nome, descrição, foto, fechar, travar, convite e participantes.
 *
 * ⚠️ **Não confere se somos administradores.** Quem sabe disso é o servidor do WhatsApp, e ele
 * responde com o erro certo. Uma checagem nossa leria o cache de `grupo_conexoes` — e cache velho
 * recusaria uma operação legítima.
 */
export function administrarGrupo(
  cliente: QueryClient,
  grupoId: string,
  corpo: {
    conexaoId: string;
    acao: "nome" | "descricao" | "fechar" | "travar" | "foto" | "convite" | "adicionar" | "remover" | "promover" | "rebaixar";
    texto?: string;
    ligado?: boolean;
    fotoBase64?: string;
    participantes?: string[];
    regerar?: boolean;
  },
) {
  return tentar(async () => {
    const r = await chamar<{ ok: true; link?: string; codigo?: string; participantes?: { numero: string; ok: boolean }[] }>(
      `/api/painel/grupos/${grupoId}/administrar`,
      { metodo: "POST", corpo },
    );
    void cliente.invalidateQueries({ queryKey: ["grupos"] });
    return r;
  });
}

export interface CanalDoWhatsApp {
  readonly id: string;
  readonly nome: string;
  readonly descricao: string;
  readonly seguidores: number;
  /** `owner`, `admin`, `subscriber`, `guest` — só o dono publica, e a tela precisa saber antes. */
  readonly papel: string;
  readonly silenciado: boolean;
  readonly convite: string;
}

/**
 * CANAIS DO WHATSAPP (Newsletter) — uma chamada, uma `acao`.
 *
 * ⚠️ Canal não é lugar de atendimento: ninguém responde nele, só reage. Nada aqui produz mensagem
 * no fio.
 */
export function operarCanal(
  cliente: QueryClient,
  corpo: {
    conexaoId: string;
    acao:
      | "listar" | "criar" | "ver" | "ver-por-convite" | "seguir" | "seguir-por-convite"
      | "deixar" | "silenciar" | "mensagens" | "reagir" | "acompanhar";
    canal?: string;
    nome?: string;
    descricao?: string;
    convite?: string;
    ligado?: boolean;
    quantas?: number;
  },
) {
  return tentar(async () => {
    const r = await chamar<{
      ok: true;
      canais?: CanalDoWhatsApp[];
      canal?: CanalDoWhatsApp;
      mensagens?: { servidorId: number; id: string; quando: string; visualizacoes: number }[];
    }>("/api/painel/canais", { metodo: "POST", corpo });
    void cliente.invalidateQueries({ queryKey: ["canais-do-whatsapp"] });
    return r;
  });
}

export function sincronizarGrupos(cliente: QueryClient, conexaoId: string) {
  return tentar(async () => {
    const r = await chamar<{ resultado: { encontrados: number; novos: number; atualizados: number; inativados: number; concluidaEm: string; gruposIncluidos: number | null; gruposGerenciados: number; foraDoPlano: number } }>("/api/painel/grupos/sincronizar", { metodo: "POST", corpo: { conexaoId } });
    void cliente.invalidateQueries({ queryKey: ["grupos"] });
    void cliente.invalidateQueries({ queryKey: ["publicacoes-resumo"] });
    return r.resultado;
  });
}

export function gerenciarGrupo(cliente: QueryClient, grupoId: string, gerenciado: boolean) {
  return tentar(async () => {
    const r = await chamar<{ gerenciados: number; gruposIncluidos: number | null }>(`/api/painel/grupos/${grupoId}/gerenciar`, { metodo: "POST", corpo: { gerenciado } });
    void cliente.invalidateQueries({ queryKey: ["grupos"] });
    return r;
  });
}

export function criarEtiquetaDeGrupo(cliente: QueryClient, nome: string, cor: string | null) {
  return tentar(async () => {
    const r = await chamar<{ etiqueta: EtiquetaDeGrupo }>("/api/painel/grupos/etiquetas", { metodo: "POST", corpo: { nome, cor } });
    void cliente.invalidateQueries({ queryKey: ["grupos"] });
    return r.etiqueta;
  });
}

export function etiquetarGrupos(cliente: QueryClient, grupos: readonly string[], adicionar: readonly string[], remover: readonly string[]) {
  return tentar(async () => {
    const r = await chamar<{ aplicadas: number; removidas: number }>("/api/painel/grupos/etiquetar", { metodo: "POST", corpo: { grupos, adicionar, remover } });
    void cliente.invalidateQueries({ queryKey: ["grupos"] });
    return r;
  });
}

export function previaDePublicacao(corpo: Record<string, unknown>) {
  return tentar(async () => (await chamar<{ previa: PreviaDaPublicacao }>("/api/painel/publicacoes/previa", { metodo: "POST", corpo })).previa);
}

export function criarPublicacao(cliente: QueryClient, corpo: Record<string, unknown>) {
  return tentar(async () => {
    const r = await chamar<{ id: string; grupos: number; primeiraOcorrencia: string | null }>("/api/painel/publicacoes", { metodo: "POST", corpo });
    invalidarPublicacoes(cliente);
    return r;
  });
}

export function guardarMidiaDePublicacao(arquivo: File) {
  return tentar(async () => {
    const base64 = await new Promise<string>((resolve, reject) => {
      const leitor = new FileReader();
      leitor.onerror = () => reject(new Error("não consegui ler o arquivo"));
      leitor.onload = () => resolve(String(leitor.result).split(",")[1] ?? "");
      leitor.readAsDataURL(arquivo);
    });
    const r = await chamar<{ midia: { id: string; tipo: "imagem" | "video" | "documento"; mime: string; nome: string; tamanho: number } }>("/api/painel/publicacoes/midias", {
      metodo: "POST", corpo: { base64, tipoMime: arquivo.type, nome: arquivo.name },
    });
    return r.midia;
  });
}

export function agirNaPublicacao(cliente: QueryClient, id: string, acao: "cancelar" | "pausar" | "retomar", corpo?: Record<string, unknown>) {
  return tentar(async () => {
    const r = await chamar<Record<string, unknown>>(`/api/painel/publicacoes/${id}/${acao}`, { metodo: "POST", corpo: corpo ?? {} });
    invalidarPublicacoes(cliente);
    return r;
  });
}

/** `de` (0097): no rodízio, QUAL número sai. Nulo = o principal, como sempre foi. */
export function trocarRemetenteDaPublicacao(cliente: QueryClient, id: string, conexaoId: string, de: string | null = null) {
  return tentar(async () => {
    const r = await chamar<Record<string, unknown>>(`/api/painel/publicacoes/${id}/remetente`, { metodo: "POST", corpo: { conexaoId, ...(de ? { de } : {}) } });
    invalidarPublicacoes(cliente);
    return r;
  });
}

// --- Saúde do número (spec 19) ---------------------------------------------------------------------

/**
 * O que a API devolve por número.
 *
 * ⚠️ `indice`, `valor` e `nota` são `number | null`, e o `null` é **carregado até a tela**. Trocá-lo
 * por `?? 0` em qualquer ponto do caminho faria "não medido" virar "zero", que é a regra que a
 * spec 19 existe para impor — e o número mais saudável do sistema (o que ninguém usou) apareceria
 * em risco.
 */
export interface IndicadorDeSaude {
  readonly nome: "entrega" | "leitura" | "resposta" | "reciprocidade" | "anomalias" | "saidasDeGrupo" | "rejeicaoEmGrupo" | "disponibilidade";
  readonly valor: number | null;
  readonly amostra: number;
  readonly peso: number;
  readonly nota: number | null;
  readonly aplicavel: boolean;
  /**
   * Por que não entrou na conta. `sem_amostra` = não houve envio; `amostra_pequena` = houve, e é
   * pouco para concluir — e neste caso o VALOR continua visível, porque ele informa.
   *
   * Opcional: um servidor mais velho não o manda, e a tela cai no comportamento antigo.
   */
  readonly motivo?: "sem_amostra" | "amostra_pequena" | null;
}

export interface PontoDaCurva {
  readonly dia: string;
  /** `null` = dia sem amostra. **Não é zero**, e a curva desenha o buraco. */
  readonly indice: number | null;
  readonly faixa: string;
  readonly componentesUsados: number;
  readonly teto: number | null;
  readonly punicoes: readonly string[];
}

export interface ExposicaoDoNumero {
  readonly porDia: readonly { readonly dia: string; readonly novos: number; readonly abordados: number }[];
  readonly ultimoDia: { readonly dia: string; readonly novos: number } | null;
  readonly mediaAnterior: number | null;
  /** Salto sobre a própria linha de base. `null` = não há com que comparar; a tela cala. */
  readonly razao: number | null;
  readonly novosNaJanela: number;
}

export interface NumeroComSaude {
  readonly conexaoId: string;
  readonly canal: string;
  readonly modo: string;
  readonly rotulo: string;
  readonly estado: string;
  /** A nota da META. `null` no aparelho vinculado — lá ela não existe, e a tela diz isso. */
  readonly qualidadeDaMeta: string | null;
  readonly qualidadeLidaEm: string | null;
  readonly tier: string | null;
  readonly limiteDiario: number | null;
  readonly frase: string;
  readonly janelaDias: number;
  readonly sinais?: Record<string, number>;
  readonly exposicao?: ExposicaoDoNumero;
  readonly curva?: readonly PontoDaCurva[];
  /** Quanto o índice andou na curva. `null` sem dois pontos — nunca zero, que se leria "estável". */
  readonly variacao?: number | null;
  readonly saude: {
    readonly indice: number | null;
    readonly faixa: "bom" | "atencao" | "risco" | "sem_amostra";
    readonly indicadores: readonly IndicadorDeSaude[];
    readonly componentes: { readonly usados: number; readonly total: number };
    readonly teto: number | null;
    readonly punicoesAtivas: readonly string[];
  };
}

export const chavesDeSaude = {
  numeros: (dias: number) => ["saude-dos-numeros", dias] as const,
  numero: (id: string, dias: number) => ["saude-do-numero", id, dias] as const,
} as const;

export function useSaudeDosNumeros(dias: number) {
  return useQuery({
    queryKey: chavesDeSaude.numeros(dias),
    queryFn: () => chamar<{ itens: readonly NumeroComSaude[] }>(`/api/painel/numeros/saude?dias=${dias}`),
    refetchInterval: 60_000,
  });
}


/** A página de UM número: a mesma medida, com a curva LONGA (90 dias) que a rota do detalhe serve. */
export function useSaudeDoNumero(id: string, dias: number) {
  return useQuery({
    queryKey: chavesDeSaude.numero(id, dias),
    queryFn: () => chamar<NumeroComSaude>(`/api/painel/numeros/${id}/saude?dias=${dias}`),
    enabled: id !== "",
    refetchInterval: 60_000,
  });
}
